import { DEFAULT_COMPACTION_SETTINGS } from "@caupulican/pi-agent-core/compaction/compaction";
import type { Transport } from "@caupulican/pi-ai";
import { createHash } from "crypto";
import { existsSync, mkdirSync, readFileSync, realpathSync, rmdirSync, rmSync, statSync, writeFileSync } from "fs";
import { homedir } from "os";
import { basename, dirname, join, relative, resolve, sep } from "path";
import { CONFIG_DIR_NAME, getAgentDir, getProfilesDir } from "../config.ts";
import { normalizePath, resolvePath } from "../utils/paths.ts";
import { configFile, directoryProfilesDir } from "./agent-paths.ts";
import { DEFAULT_CONTEXT_GC_SETTINGS } from "./context-gc.ts";
import { type CostGuardSettings, DEFAULT_COST_GUARD_SETTINGS } from "./cost-guard.ts";
import {
	DEFAULT_GOAL_AUTO_CONTINUE,
	DEFAULT_GOAL_AUTO_CONTINUE_DELAY_MS,
	DEFAULT_GOAL_CONTINUE_MAX_STALL_TURNS,
	DEFAULT_GOAL_CONTINUE_MAX_TURNS,
	DEFAULT_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES,
	MAX_GOAL_AUTO_CONTINUE_DELAY_MS,
	MAX_GOAL_CONTINUE_MAX_STALL_TURNS,
	MAX_GOAL_CONTINUE_MAX_TURNS,
	MAX_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES,
} from "./goals/goal-continuation-defaults.ts";
import { DEFAULT_HTTP_IDLE_TIMEOUT_MS, parseHttpIdleTimeoutMs } from "./http-dispatcher.ts";
import { MAX_ORCHESTRATION_IDENTIFIER_LENGTH } from "./orchestration/contracts.ts";
import {
	compileWorkerModelPinPolicy,
	type WorkerModelPinPolicy,
	type WorkerModelPinsSettings,
} from "./orchestration/worker-model-pins.ts";
import { ProfileRegistry } from "./profile-registry.ts";
import {
	mergeResourceProfileMap,
	mergeResourceProfileSettings,
	normalizeResourceProfileFilter,
} from "./resource-profile-blocks.ts";
import { isWorkerSession } from "./session-role.ts";
import { validateSkillName } from "./skills.ts";
import type { ToolkitScript } from "./toolkit/script-registry.ts";
import { acquireFileLockSync, LOW_LATENCY_FILE_LOCK_OPTIONS, writeFileAtomicSync } from "./util/atomic-file.ts";
import { matchesCompiledPattern } from "./util/minimatch-cache.ts";
import { isPlainRecord } from "./util/value-guards.ts";

export interface CompactionSettings {
	enabled?: boolean; // default: true
	reserveTokens?: number; // default: 16384
	keepRecentTokens?: number; // default: 20000
	triggerPercent?: number; // default: 0.6 — early context-efficiency threshold, separate from the USD cost guard
	model?: string; // default: "auto" — cheap auxiliary model for the summary; "auto" picks cheapest authed, else the session model
}

export interface ScoutSettings {
	enabled?: boolean; // default: false
	model?: string; // default: "auto" — resolve an installed FastContext model, else return unavailable from the tool
}

export interface SemanticMemoryGcSettings {
	enabled?: boolean; // default: true
	preserveRecentPages?: number; // default: 1 -- see context-gc.ts DEFAULT_CONTEXT_GC_SETTINGS (canonical)
	minChars?: number; // default: 900 -- see context-gc.ts DEFAULT_CONTEXT_GC_SETTINGS (canonical)
	markers?: string[]; // default: memory/automata recall markers + the task_steps_context checklist marker
}

export interface ContextGcSettings {
	enabled?: boolean; // default: true
	preserveRecentMessages?: number; // default: 24 -- see context-gc.ts DEFAULT_CONTEXT_GC_SETTINGS (canonical)
	minToolResultChars?: number; // default: 1200
	tools?: string[]; // default: read,bash,rg,grep,context_headroom_retrieve,headroom_retrieve
	semanticMemory?: SemanticMemoryGcSettings;
}

/**
 * Conservative, opt-in first enforcement pilot for the context-policy layer (observe-only
 * by default -- see context/context-prompt-enforcement.ts). When enabled, stale
 * artifact-backed tool_output results outside the recent window are stubbed in place in
 * the provider-visible prompt only; the transcript/session history is never touched.
 */
export interface ContextPromptEnforcementSettings {
	enabled?: boolean; // default: false -- no behavior change unless explicitly opted in
	// default: mirrors the live contextGc.preserveRecentMessages (getContextGcSettings()), so tuning
	// GC's window moves this default with it; an explicit value here always wins over the mirror.
	preserveRecentMessages?: number;
	// default: mirrors the live contextGc.minToolResultChars (getContextGcSettings()); an explicit
	// value here always wins over the mirror.
	minChars?: number;
}

/**
 * Local memory retrieval (see context/memory-retrieval.ts, context/memory-prompt-block.ts):
 * default-on for local, safe-auto sources. Prompt inclusion is still budget-gated per turn;
 * compact models get at most a 10-line/~200-token source-labeled block or no memory block.
 * External/non-local providers remain blocked unless explicitly allowed by policy.
 */
export interface MemoryRetrievalSettings {
	enabled?: boolean; // default: true -- local safe-auto retrieval
	maxResults?: number; // default: 5, clamped to [1, 20]
	includeInPrompt?: boolean; // default: true -- budget-gated safe-auto prompt inclusion
	allowExternalEgress?: boolean; // default: false -- explicit opt-in for raw query egress
}

export interface ContextCurationSettings {
	enabled?: boolean; // default: false -- the curator never runs unless explicitly opted in
	/** Local model ref ("provider/id" or bare id) used for curation jobs. Required to drain. */
	model?: string;
	maxJobsPerTurn?: number; // default: 4, clamped to [1, 16]
}

export interface ContextPolicySettings {
	enforcement?: ContextPromptEnforcementSettings;
	memory?: MemoryRetrievalSettings;
	curation?: ContextCurationSettings;
}

export const MEMORY_RETRIEVAL_MAX_RESULTS_MIN = 1;
export const MEMORY_RETRIEVAL_MAX_RESULTS_MAX = 20;
const MEMORY_RETRIEVAL_MAX_RESULTS_DEFAULT = 5;

function clampMemoryRetrievalMaxResults(value: number): number {
	return Math.min(MEMORY_RETRIEVAL_MAX_RESULTS_MAX, Math.max(MEMORY_RETRIEVAL_MAX_RESULTS_MIN, Math.trunc(value)));
}

export interface BranchSummarySettings {
	reserveTokens?: number; // default: 16384 (tokens reserved for prompt + LLM response)
	skipPrompt?: boolean; // default: false - when true, skips "Summarize branch?" prompt and defaults to no summary
}

export interface ProviderRetrySettings {
	timeoutMs?: number; // SDK/provider request timeout in milliseconds
	maxRetries?: number; // SDK/provider retry attempts
	maxRetryDelayMs?: number; // default: 60000 (max server-requested delay before failing)
}

export interface StreamStallSettings {
	connectMs?: number; // default: 120000 — max wait for the first stream event
	activeIdleMs?: number; // default: 180000 — max event gap while content is flowing
	quietIdleMs?: number; // default: 600000 — max event gap during prefill/unstreamed thinking; clamped below nonzero httpIdleTimeoutMs
}

export interface RetrySettings {
	enabled?: boolean; // default: true
	maxRetries?: number; // default: 3
	baseDelayMs?: number; // default: 2000 (exponential backoff: 2s, 4s, 8s)
	provider?: ProviderRetrySettings;
	stall?: StreamStallSettings; // stream-stall watchdog bounds (pi-agent-core reliability/watchdogs.ts)
}

export interface TerminalSettings {
	showImages?: boolean; // default: true (only relevant if terminal supports images)
	imageWidthCells?: number; // default: 60 (preferred inline image width in terminal cells)
	clearOnShrink?: boolean; // default: false (clear empty rows when content shrinks)
	showTerminalProgress?: boolean; // default: false (OSC 9;4 terminal progress indicators)
}

export interface ImageSettings {
	autoResize?: boolean; // default: true (resize images to 2000x2000 max for better model compatibility)
	blockImages?: boolean; // default: false - when true, prevents all images from being sent to LLM providers
	clipboardDirectory?: string; // default: <agentDir>/state/attachments; relative paths resolve from the active cwd
}

export interface ThinkingBudgetsSettings {
	minimal?: number;
	low?: number;
	medium?: number;
	high?: number;
}

export interface MarkdownSettings {
	codeBlockIndent?: string; // default: "  "
}

export interface WarningSettings {
	anthropicExtraUsage?: boolean; // default: true
}

export interface SelfModificationSettings {
	enabled?: boolean; // default: false
	sourcePath?: string; // Single pi-adaptative source tree path (legacy; still honored)
	sourcePaths?: string[]; // Ordered candidate source trees; first existing wins. Enables portable WSL/Termux switching from settings alone.
}

export type AutoLearnThinkingLevel = "off" | "minimal" | "low" | "medium" | "high" | "xhigh" | "max" | "ultra";

export interface AutoLearnSettings {
	enabled?: boolean; // effective default: true in every autonomy preset; explicit false is the kill switch
	model?: string; // "active" or omitted uses the current session model; otherwise a pi --model pattern
	thinkingLevel?: AutoLearnThinkingLevel; // default: low for background learner subprocesses
	longSessionMessages?: number; // preset default: 32 in off, 64 otherwise
	longSessionContextPercent?: number; // preset default: 70 in off, 85 otherwise
	cooldownMinutes?: number; // default: 1440 per session tenant (manual /auto-learn run bypasses)
	leaseMinutes?: number; // default: 90 for background learner state leases
	maxConcurrentLearners?: number; // preset default: 1 per session tenant
	applyHighConfidence?: boolean; // default: false unless the learning extension config opts in
	reflectionReview?: boolean; // default: true when Auto Learn is enabled - include one root-session cue on eligible external turns; completed-turn signals coalesce
	reflectionMinToolCalls?: number; // default: 12 tool calls in a turn before a root-session reflection cue is queued
	reflectionCooldownMinutes?: number; // default: 1440 per session tenant between root-session reflection cues
	complexTaskToolCalls?: number; // default: 12 tool calls before bypassing reflection cooldown as a complex task
}

export type AutonomyMode = "off" | "safe" | "balanced" | "full";

export const DEFAULT_AUTONOMY_MAX_STALL_TURNS = DEFAULT_GOAL_CONTINUE_MAX_STALL_TURNS;
export const DEFAULT_AUTONOMY_GOAL_CONTINUE_TURNS = DEFAULT_GOAL_CONTINUE_MAX_TURNS;
export const DEFAULT_AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES = DEFAULT_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES;
export const DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE = DEFAULT_GOAL_AUTO_CONTINUE;
export const DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS = DEFAULT_GOAL_AUTO_CONTINUE_DELAY_MS;

export interface AutonomySettings {
	mode?: AutonomyMode; // default: off; presets drive Auto Learn/reflection without many knobs
	maxStallTurns?: number; // default: 20; unchanged-turn threshold that forces a different autonomous recovery approach
	goalContinueTurns?: number; // default: 0 (unbounded); a positive value is an explicit per-loop limit
	goalContinueMaxWallClockMinutes?: number; // default: 0; 0 disables wall-clock budget
	goalAutoContinue?: boolean; // default: true; auto-inject continuation prompts when an active goal is idle
	goalAutoContinueDelayMs?: number; // default: 0; delay before idle auto-continuation starts
}

export interface FailoverSettings {
	subscriptionHop?: boolean; // default: true; subscription quota can hop once to an authenticated provider default
}

export interface ModelRouterSettings {
	enabled?: boolean; // default: false — routing is opt-in until escalation safeguards are complete
	cheapModel?: string; // model pattern for read-only/research turns
	mediumModel?: string; // model pattern for normal scoped implementation, edits, and refactors
	expensiveModel?: string; // model pattern for modify/tool-heavy turns
	learningModel?: string; // model pattern for explicit/background learning and skill-creator work; automatic reflection uses the current session turn
	judgeEnabled?: boolean; // default: true — the routing judge runs automatically whenever the router is enabled and a judge model resolves
	fitnessGate?: boolean; // default: false — opt-in; blocks tier models whose probed relevant lane failed (Class B, subtractive)
	judgeModel?: string; // model pattern for the routing-only judge; unset falls back to mediumModel
	executorModel?: string; // model pattern for the local executor lane (direct toolkit commands); unset disables it
	// Per-tier thinking: overrides the inherited-and-clamped session thinking level for a routed
	// turn on that tier only (see agent-session.ts's routed-turn swap). Unset reproduces today's
	// behavior exactly — inherit the session thinking level, clamped to the routed model. learningModel
	// already has its own thinking via autoLearn.thinkingLevel, so there is deliberately no learningThinking.
	cheapThinking?: ThinkingLevel;
	mediumThinking?: ThinkingLevel;
	expensiveThinking?: ThinkingLevel;
	executorThinking?: ThinkingLevel; // thinking level for the executor-direct lane
	judgeThinking?: ThinkingLevel; // thinking level for the routing judge's own completion; unset keeps today's "off"
}

export const DEFAULT_RESEARCH_LANE_ENABLED = false;
export const DEFAULT_RESEARCH_LANE_MAX_USD = 0.25;
export const DEFAULT_RESEARCH_LANE_MAX_SOURCES = 8;
export const DEFAULT_RESEARCH_LANE_MAX_FINDINGS = 10;
export const DEFAULT_RESEARCH_LANE_MAX_WALL_CLOCK_MS = 120_000;
export const DEFAULT_RESEARCH_LANE_IDLE_DELAY_MS = 0;
export const DEFAULT_RESEARCH_LANE_MAX_RUNS_PER_SESSION = 10;
export const MAX_RESEARCH_LANE_MAX_USD = 5;
export const MAX_RESEARCH_LANE_MAX_SOURCES = 32;
export const MAX_RESEARCH_LANE_MAX_FINDINGS = 50;
export const MAX_RESEARCH_LANE_MAX_WALL_CLOCK_MS = 3_600_000;
export const MAX_RESEARCH_LANE_IDLE_DELAY_MS = 300_000;
export const MAX_RESEARCH_LANE_MAX_RUNS_PER_SESSION = 100;

export interface ResearchLaneSettings {
	enabled?: boolean; // default: false — autonomous background research is opt-in
	model?: string; // model pattern; unset inherits the session model the lane was shipped from
	profile?: string; // shipped profile; model/soul/thinking plus grants over classified read-only lane tools govern it
	systemPrompt?: string; // replaces the lane role prompt (the level-0 subagent core always remains)
	maxUsd?: number; // default: 0.25 per research pass; post-hoc breaches mark the lane budget_exhausted
	maxSources?: number; // default: 8 evidence sources per bundle
	maxFindings?: number; // default: 10 findings per bundle
	maxWallClockMs?: number; // default: 120000; 0 disables the wall-clock budget
	idleDelayMs?: number; // default: 0 — delay before idle-triggered research starts
	maxRunsPerSession?: number; // default: 10 idle-triggered research passes per session
}

export type ResolvedResearchLaneSettings = Required<Omit<ResearchLaneSettings, "model" | "profile" | "systemPrompt">> &
	Pick<ResearchLaneSettings, "model" | "profile" | "systemPrompt">;

export const DEFAULT_WORKER_DELEGATION_ENABLED = true;
export const DEFAULT_WORKER_DELEGATION_MAX_USD = 0;
export const DEFAULT_WORKER_DELEGATION_MAX_WALL_CLOCK_MS = 0;
export const DEFAULT_WORKER_DELEGATION_MAX_CONCURRENT = 20;
export const DEFAULT_WORKER_DELEGATION_WRITE_ENABLED = true;
export const MAX_WORKER_DELEGATION_MAX_USD = Number.MAX_SAFE_INTEGER;
export const MAX_WORKER_DELEGATION_MAX_WALL_CLOCK_MS = Number.MAX_SAFE_INTEGER;
export const MAX_WORKER_DELEGATION_MAX_CONCURRENT = Number.MAX_SAFE_INTEGER;

export interface WorkerDelegationSettings {
	enabled?: boolean; // default: true for capable models; explicit false is a hard off-switch
	orchestrationProfile?: string; // optional execution preset; agents may replace its defaults within inherited authority
	maxUsd?: number; // default: 0 (unbounded); a positive value caps spend for one worker task
	maxWallClockMs?: number; // default: 0 (unbounded); a positive value caps one worker task's cumulative active time
	writeEnabled?: boolean; // default: true; explicit false revokes direct write/edit tools
	maxConcurrent?: number; // default: 20; running leaf-worker concurrency; fixed fleet safety ceilings separately bound durable identities and queued dispatches
	modelPins?: WorkerModelPinsSettings; // optional global/local role pins; absent preserves adaptive routing exactly
}

export type ResolvedWorkerDelegationSettings = Required<
	Omit<WorkerDelegationSettings, "orchestrationProfile" | "modelPins">
> &
	Pick<WorkerDelegationSettings, "orchestrationProfile">;

/** Staleness-propagation policy for worktree-sync; see `core/worktree-sync/codes.ts`. */
export type WorktreeSyncPolicySetting = "on_land_mandatory" | "overlap_mandatory" | "land_time_only";

export interface WorktreeSyncSettings {
	enabled?: boolean; // default: true -- master switch; explicit false is the hard off-switch (zero behavior change when off)
	mainBranch?: string; // overrides default-branch resolution (main, then master; never guessed further)
	syncPolicy?: WorktreeSyncPolicySetting; // default: "on_land_mandatory" -- every land marks every other active lane sync_required
	gateCommand?: string; // land gate command run in the lane worktree at the exact tip that becomes main (e.g. "npm run check")
	gate?: "on" | "off"; // default: "on" -- "off" is the owner-level G4 opt-out, recorded per land event
	gateTimeoutMs?: number; // default: 900000
	maxLanes?: number; // default: 5 -- active-lane ceiling, matches the <=5-coder orchestration shape
	worktreesRoot?: string; // overrides the default lane-checkout root (agent-paths worktreesDir)
	workerLand?: "deny" | "allow"; // default: "deny" -- whether a WORKER session (see session-role.ts) may run the "land" action at all; "allow" still subjects the land to normal ownership/freshness gating
}

export type ResolvedWorktreeSyncSettings = Required<
	Omit<WorktreeSyncSettings, "mainBranch" | "gateCommand" | "worktreesRoot">
> &
	Pick<WorktreeSyncSettings, "mainBranch" | "gateCommand" | "worktreesRoot">;

export const DEFAULT_WORKTREE_SYNC_POLICY: WorktreeSyncPolicySetting = "on_land_mandatory";
export const DEFAULT_WORKTREE_SYNC_GATE_TIMEOUT_MS = 900_000;
export const DEFAULT_WORKTREE_SYNC_MAX_LANES = 5;

/** Durable master/worker process-matrix supervision; see `core/process-matrix/`. */
export interface ProcessMatrixSettings {
	enabled?: boolean; // default: true -- master switch; explicit false is the hard off-switch (zero behavior change when off)
	heartbeatMs?: number; // default: 30000 -- master heartbeat interval
	adoptionGraceMs?: number; // default: 300000 -- how long an orphaned worker waits (polling for an adopt/cleanup directive) before self-exiting
	watcherPollMs?: number; // default: 25000 -- poll cadence for parent-liveness / directive checks
}

export type ResolvedProcessMatrixSettings = Required<ProcessMatrixSettings>;

export const DEFAULT_PROCESS_MATRIX_HEARTBEAT_MS = 30_000;
export const DEFAULT_PROCESS_MATRIX_ADOPTION_GRACE_MS = 300_000;
export const DEFAULT_PROCESS_MATRIX_WATCHER_POLL_MS = 25_000;

/** Windows shell contract engine tier (`src/core/tools/windows-shell-engine.ts`). */
export interface WindowsShellSettings {
	pythonEngine?: boolean; // default: true -- routes complex/state-mutating Bash constructs to the bundled Python engine on Windows; explicit false restores the PowerShell-only floor verbatim
}

export type ResolvedWindowsShellSettings = Required<WindowsShellSettings>;

export type LearningPolicyLayer =
	| "memory"
	| "skill"
	| "prompt"
	| "extension"
	| "tool"
	| "script"
	| "settings"
	| "source";

export const DEFAULT_LEARNING_POLICY_ENABLED = true;
export const DEFAULT_LEARNING_POLICY_AUTO_APPLY_ENABLED = true;
export const DEFAULT_LEARNING_POLICY_CONFIDENCE_THRESHOLD = 50;
export const DEFAULT_LEARNING_POLICY_MIN_OBSERVATIONS = 1;
export const DEFAULT_LEARNING_POLICY_ALLOWED_AUTO_APPLY_LAYERS: readonly LearningPolicyLayer[] = ["memory", "skill"];
export const DEFAULT_LEARNING_POLICY_REFLECTION_SOURCE_CONFIDENCE = 50;
export const DEFAULT_LEARNING_POLICY_AUTO_APPLY_SUPERSESSIONS = false;

export interface LearningPolicySettings {
	enabled?: boolean; // default: true — main-session reflection routes durable writes through the audited policy
	autoApplyEnabled?: boolean; // default: true — safe additive/organizational writes may apply without user babysitting
	confidenceThreshold?: number; // default: 50 (0-100), matching the default reflection-source confidence
	minObservations?: number; // default: 1 — a main-session reflection may apply a first safe observation
	allowedAutoApplyLayers?: LearningPolicyLayer[]; // default: ["memory", "skill"] — every other layer stays proposal-first
	requireRollbackPlan?: boolean; // default: true — durable writes need a rollback plan to auto-apply
	reflectionSourceConfidence?: number; // default: 50 — trust assigned to single-session reflection cues (0-100)
	autoApplySupersessions?: boolean; // default: false — a memory_replace/memory_remove (supersedes/deletes an existing fact) stays a proposal even when otherwise eligible, unless explicitly opted in
}

export type ResolvedLearningPolicySettings = Required<LearningPolicySettings>;

export interface ToolkitSettings {
	/** The blessed daily-ops scripts run_toolkit_script may execute. Nothing else ever runs. */
	scripts?: ToolkitScript[];
}

export type ModelCapabilityMode = "auto" | "off" | "full" | "lean" | "minimal" | "chat";

export const DEFAULT_MODEL_CAPABILITY_MODE: ModelCapabilityMode = "auto";

export interface ModelCapabilitySettings {
	/**
	 * default: "auto" — derive the tool/lane surface from the model's context window so small open
	 * models stay usable for chat. "off" disables detection; a class name forces that class.
	 */
	mode?: ModelCapabilityMode;
}

export type BedrockScopeVerification = "identity+control-plane+runtime" | "runtime";

/** User-owned, verified Amazon Bedrock request and model-visibility boundary. */
export interface BedrockScopeSettings {
	region: string;
	profile?: string;
	modelIds: string[];
	verifiedAt: string;
	verification: BedrockScopeVerification;
}

const MAX_BEDROCK_SCOPE_MODEL_IDS = 16;
const MAX_BEDROCK_MODEL_ID_CHARS = 512;

function normalizeBedrockScopeSettings(value: BedrockScopeSettings | undefined): BedrockScopeSettings | undefined {
	if (!value || typeof value !== "object") return undefined;
	const region = typeof value.region === "string" ? value.region.trim().toLowerCase() : "";
	if (!/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(region)) return undefined;

	const profile = typeof value.profile === "string" ? value.profile.trim() : undefined;
	if (
		profile !== undefined &&
		(profile.length === 0 || profile.length > 256 || /[\u0000-\u001f\u007f]/.test(profile))
	) {
		return undefined;
	}

	if (!Array.isArray(value.modelIds)) return undefined;
	const modelIds = [
		...new Set(
			value.modelIds
				.filter((id): id is string => typeof id === "string")
				.map((id) => id.trim())
				.filter(
					(id) => id.length > 0 && id.length <= MAX_BEDROCK_MODEL_ID_CHARS && !/[\u0000-\u001f\u007f]/.test(id),
				),
		),
	];
	if (modelIds.length === 0 || modelIds.length > MAX_BEDROCK_SCOPE_MODEL_IDS) return undefined;

	const verifiedAt = typeof value.verifiedAt === "string" ? value.verifiedAt.trim() : "";
	if (!verifiedAt || !Number.isFinite(Date.parse(verifiedAt))) return undefined;
	if (value.verification !== "identity+control-plane+runtime" && value.verification !== "runtime") {
		return undefined;
	}

	return {
		region,
		...(profile ? { profile } : {}),
		modelIds,
		verifiedAt,
		verification: value.verification,
	};
}

export type TransportSetting = Transport;

/**
 * Package source for npm/git packages.
 * - String form: load all resources from the package
 * - Object form: filter which resources to load
 */
export type PackageSource =
	| string
	| {
			source: string;
			extensions?: string[];
			skills?: string[];
			prompts?: string[];
			themes?: string[];
	  };

export type ResourceProfileKind = "extensions" | "skills" | "prompts" | "themes" | "agents" | "tools";

export interface ResourceProfileFilterSettings {
	/** Allowlist patterns. When non-empty, only matching resources stay available. */
	allow?: string[];
	/** Blocklist patterns. Applied after allow. */
	block?: string[];
}

export type ResourceProfileSettings = Partial<Record<ResourceProfileKind, ResourceProfileFilterSettings>>;

export interface DisabledResourcesSettings {
	extensions?: string[];
	skills?: string[];
	prompts?: string[];
	themes?: string[];
	agents?: string[];
	tools?: string[];
}

export interface ToolRepairSettings {
	teach?: boolean;
	textProtocol?: boolean;
	logging?: boolean;
}

export interface Settings {
	lastChangelogVersion?: string;
	defaultProvider?: string;
	defaultModel?: string;
	defaultThinkingLevel?: ThinkingLevel;
	/** Provider-scoped fast-mode preferences. Concrete providers own the meaning of enabled. */
	fastMode?: Record<string, boolean>;
	transport?: TransportSetting; // default: "auto"
	steeringMode?: "all" | "one-at-a-time";
	followUpMode?: "all" | "one-at-a-time";
	theme?: string;
	/** Resource catalog directory (round resource management): the folder pi installs/updates/backs up from. */
	catalogDir?: string;
	compaction?: CompactionSettings;
	scout?: ScoutSettings;
	/** Proactive per-turn cost guard (#34). */
	costGuard?: Partial<CostGuardSettings>;
	/** Skill curator (#32): auto-archive stale reflection-promoted skills at session start. */
	curator?: { autoArchive?: boolean; staleDays?: number };
	contextGc?: ContextGcSettings;
	contextPolicy?: ContextPolicySettings;
	branchSummary?: BranchSummarySettings;
	retry?: RetrySettings;
	hideThinkingBlock?: boolean;
	shellPath?: string; // Custom shell path (e.g., for Cygwin users on Windows)
	quietStartup?: boolean;
	/**
	 * How to treat repository AGENTS.md/CLAUDE.md/GEMINI.md files.
	 * `"off"` (default): global `~/.pi/agent` files only; do not walk project files.
	 * `"on-demand"`: opt in for this settings scope — list project paths, do not inject contents.
	 * Persist per directory (directory overlay), per project (`.pi/settings.json`), or globally.
	 */
	projectContextFiles?: "on-demand" | "off";
	shellCommandPrefix?: string; // Prefix prepended to every bash command (e.g., "shopt -s expand_aliases" for alias support)
	npmCommand?: string[]; // Command used for npm package lookup/install operations, argv-style (e.g., ["mise", "exec", "node@20", "--", "npm"])
	collapseChangelog?: boolean; // Show condensed changelog after update (use /changelog for full)
	enableInstallTelemetry?: boolean; // default: true - anonymous version/update ping after changelog-detected updates
	packages?: PackageSource[]; // Array of npm/git package sources (string or object with filtering)
	extensions?: string[]; // Array of local extension file paths/directories or include/exclude patterns
	skills?: string[]; // Array of local skill file paths/directories or include/exclude patterns
	prompts?: string[]; // Array of local prompt template paths/directories or include/exclude patterns
	themes?: string[]; // Array of local theme file paths/directories or include/exclude patterns
	externalResourceRoots?: string[]; // External directory roots to scan for resources
	trustedResourceRoots?: string[]; // Explicitly trusted external directory roots (canonical absolute paths)
	disabledResources?: DisabledResourcesSettings; // Legacy reversible block filters for extensions/skills/prompts/themes/agents/tools
	resourceProfiles?: Record<string, ResourceProfileSettings | ProfileDefinitionInput>; // Named resource filters, optionally with full situation metadata
	activeResourceProfile?: string | string[]; // Active profile name(s), applied after global/project/directory settings merge
	activeResourceProfiles?: string[]; // Active profile names, equivalent to activeResourceProfile array
	activeOrchestrationProfile?: string; // owner-authored role/model/thinking/tools/resources/budget profile for the foreground session
	enableSkillCommands?: boolean; // default: true - register skills as /skill:name commands
	terminal?: TerminalSettings;
	images?: ImageSettings;
	enabledModels?: string[]; // Model patterns for cycling (same format as --models CLI flag)
	doubleEscapeAction?: "fork" | "tree" | "none"; // Action for double-escape with empty editor (default: "tree")
	treeFilterMode?: "default" | "no-tools" | "user-only" | "labeled-only" | "all"; // Default filter when opening /tree
	thinkingBudgets?: ThinkingBudgetsSettings; // Custom token budgets for thinking levels
	editorPaddingX?: number; // Horizontal padding for input editor (default: 0)
	autocompleteMaxVisible?: number; // Max visible items in autocomplete dropdown (default: 5)
	showHardwareCursor?: boolean; // Show terminal cursor while still positioning it for IME
	markdown?: MarkdownSettings;
	warnings?: WarningSettings;
	selfModification?: SelfModificationSettings; // Local guardrails for modifying the pi-adaptative source/harness
	autonomy?: AutonomySettings; // Low-config autonomy preset controlling background learning/reflection defaults
	researchLane?: ResearchLaneSettings; // Opt-in autonomous read-only research lane producing evidence bundles
	workerDelegation?: WorkerDelegationSettings; // Autonomous persistent leaf-worker scheduling; enabled by default on capable models
	worktreeSync?: WorktreeSyncSettings; // Opt-in hard-gated worktree-per-lane parallel-work workflow (core/worktree-sync)
	processMatrix?: ProcessMatrixSettings; // Durable master/worker process-matrix supervision (core/process-matrix); on by default
	windowsShell?: WindowsShellSettings; // Windows shell contract engine tier (core/tools/windows-shell-engine); on by default
	learningPolicy?: LearningPolicySettings; // Default-on audited learning policy; destructive supersessions remain proposal-gated
	modelCapability?: ModelCapabilitySettings; // Auto-detected small-model tool/lane surface (default: auto)
	bedrock?: BedrockScopeSettings; // User-level verified profile/region/model scope for Amazon Bedrock
	toolkit?: ToolkitSettings; // User's blessed daily-ops script registry for run_toolkit_script
	modelRouter?: ModelRouterSettings; // Opt-in deterministic cheap/expensive model routing foundation
	toolRepair?: ToolRepairSettings; // Tool-recovery logging plus teach and text-protocol switches
	failover?: FailoverSettings; // Provider quota behavior; metered quota always halts for explicit user choice
	autoLearn?: AutoLearnSettings; // Root current-session reflection plus explicitly invoked Auto Learn compatibility settings
	sessionDir?: string; // Custom session storage directory (same format as --session-dir CLI flag)
	httpIdleTimeoutMs?: number; // HTTP header/body idle timeout in ms; 0 disables it. Nonzero values constrain every stream-watchdog phase below this timeout
	websocketConnectTimeoutMs?: number; // WebSocket connect/open handshake timeout in milliseconds; 0 disables it
}

/** Deep merge settings: project/overrides take precedence, nested objects merge recursively */
function deepMergeSettings(base: Settings, overrides: Settings): Settings {
	const result: Settings = { ...base };

	for (const key of Object.keys(overrides) as (keyof Settings)[]) {
		const overrideValue = overrides[key];
		const baseValue = base[key];

		if (overrideValue === undefined) {
			continue;
		}

		// For nested objects, merge recursively
		if (
			typeof overrideValue === "object" &&
			overrideValue !== null &&
			!Array.isArray(overrideValue) &&
			typeof baseValue === "object" &&
			baseValue !== null &&
			!Array.isArray(baseValue)
		) {
			(result as Record<string, unknown>)[key] = deepMergeSettings(baseValue as Settings, overrideValue as Settings);
		} else {
			// For primitives and arrays, override value wins
			(result as Record<string, unknown>)[key] = overrideValue;
		}
	}

	return result;
}

function toPosixPath(p: string): string {
	return p.split(sep).join("/");
}

function findDirectoryProfileRoot(cwd: string): string {
	let current = resolvePath(cwd);
	while (true) {
		for (const marker of [".git", ".hg", ".svn"]) {
			try {
				statSync(join(current, marker));
				return current;
			} catch {}
		}
		const parent = resolve(current, "..");
		if (parent === current) return resolvePath(cwd);
		current = parent;
	}
}

export interface DirectoryResourceProfileInfo {
	root: string;
	hash: string;
	path: string;
}

export function getDirectoryResourceProfileInfo(
	cwd: string,
	agentDir: string = getAgentDir(),
): DirectoryResourceProfileInfo {
	const root = findDirectoryProfileRoot(cwd);
	const hash = createHash("sha256").update(root).digest("hex").slice(0, 16);
	return {
		root,
		hash,
		path: join(directoryProfilesDir(resolvePath(agentDir)), hash, "settings.json"),
	};
}

function getResourceProfileMatchCandidates(resourcePath: string, baseDir: string): readonly string[] {
	const resolvedBase = baseDir ? resolvePath(baseDir) : "";
	const rel = resolvedBase ? toPosixPath(relative(resolvedBase, resourcePath)) : toPosixPath(resourcePath);
	const name = basename(resourcePath);
	const filePathPosix = toPosixPath(resourcePath);
	const parentDir = dirname(resourcePath);
	const parentRel = resolvedBase ? toPosixPath(relative(resolvedBase, parentDir)) : toPosixPath(parentDir);
	const parentName = basename(parentDir);
	const parentDirPosix = toPosixPath(parentDir);
	return [rel, name, filePathPosix, parentRel, parentName, parentDirPosix];
}

export function matchesResourceProfilePattern(resourcePath: string, patterns: string[], baseDir = ""): boolean {
	if (patterns.length === 0) return false;
	const candidates = getResourceProfileMatchCandidates(resourcePath, baseDir);
	return patterns.some((pattern) => {
		const normalizedPattern = toPosixPath(pattern);
		return candidates.some((candidate) => matchesCompiledPattern(candidate, normalizedPattern));
	});
}

function matchesExactResourceProfilePattern(resourcePath: string, patterns: string[], baseDir: string): boolean {
	if (patterns.length === 0) return false;
	const candidates = getResourceProfileMatchCandidates(resourcePath, baseDir);
	return patterns.some((pattern) => {
		const withoutRelativePrefix = pattern.startsWith("./") || pattern.startsWith(".\\") ? pattern.slice(2) : pattern;
		return candidates.includes(toPosixPath(withoutRelativePrefix));
	});
}

/** Canonical `!`/`+`/`-` precedence for top-level resource selector overrides. */
export function isResourceEnabledByTopLevelOverrides(resourcePath: string, entries: string[], baseDir = ""): boolean {
	const overrides = entries.filter(
		(pattern) => pattern.startsWith("!") || pattern.startsWith("+") || pattern.startsWith("-"),
	);
	const excludes = overrides.filter((pattern) => pattern.startsWith("!")).map((pattern) => pattern.slice(1));
	const forceIncludes = overrides.filter((pattern) => pattern.startsWith("+")).map((pattern) => pattern.slice(1));
	const forceExcludes = overrides.filter((pattern) => pattern.startsWith("-")).map((pattern) => pattern.slice(1));

	let enabled = !matchesResourceProfilePattern(resourcePath, excludes, baseDir);
	if (matchesExactResourceProfilePattern(resourcePath, forceIncludes, baseDir)) enabled = true;
	if (matchesExactResourceProfilePattern(resourcePath, forceExcludes, baseDir)) enabled = false;
	return enabled;
}

function normalizeResourceProfileNames(value: unknown): string[] {
	const values: string[] = [];
	const add = (candidate: unknown) => {
		if (Array.isArray(candidate)) {
			for (const item of candidate) add(item);
			return;
		}
		if (typeof candidate === "string") {
			for (const part of candidate.split(",")) {
				const trimmed = part.trim();
				if (trimmed) values.push(trimmed);
			}
		}
	};
	add(value);
	return [...new Set(values)];
}

function hasExplicitActiveResourceProfileSelection(settings: Settings): boolean {
	return Object.hasOwn(settings, "activeResourceProfiles") || Object.hasOwn(settings, "activeResourceProfile");
}

function hasExplicitEmptyActiveResourceProfileSelection(settings: Settings): boolean {
	return (
		Object.hasOwn(settings, "activeResourceProfiles") &&
		normalizeResourceProfileNames(settings.activeResourceProfiles).length === 0
	);
}

function normalizeActiveResourceProfiles(settings: Settings): string[] {
	// The array form is canonical when present, including an explicit empty array. Falling through
	// from `activeResourceProfiles: []` to the scalar alias would resurrect a lower-precedence or
	// legacy selection and makes it impossible to persist a durable "none" choice.
	const values = Object.hasOwn(settings, "activeResourceProfiles")
		? normalizeResourceProfileNames(settings.activeResourceProfiles)
		: normalizeResourceProfileNames(settings.activeResourceProfile);
	if (
		values.length === 0 &&
		!hasExplicitActiveResourceProfileSelection(settings) &&
		settings.resourceProfiles?.default
	) {
		values.push("default");
	}
	return [...new Set(values)];
}

function appendFilter(target: ResourceProfileFilterSettings, source?: ResourceProfileFilterSettings): void {
	if (!source) return;
	if (Array.isArray(source.allow)) target.allow = [...(target.allow ?? []), ...source.allow];
	if (Array.isArray(source.block)) target.block = [...(target.block ?? []), ...source.block];
}

function collectLegacyDisabledFilterFromSettings(
	settings: Settings,
	kind: ResourceProfileKind,
): ResourceProfileFilterSettings {
	const legacyDisabled = settings.disabledResources?.[kind];
	return Array.isArray(legacyDisabled) ? { block: legacyDisabled } : {};
}

function isResourceDisabledByTopLevelOverrides(
	settings: Settings,
	kind: ResourceProfileKind,
	resourcePath: string,
	baseDir: string,
): boolean {
	if (kind === "agents" || kind === "tools") return false;
	const entries = settings[kind];
	if (!Array.isArray(entries)) return false;
	return !isResourceEnabledByTopLevelOverrides(resourcePath, entries, baseDir);
}

function mergeResourceProfileFilters(...filters: ResourceProfileFilterSettings[]): ResourceProfileFilterSettings {
	const result: ResourceProfileFilterSettings = {};
	for (const filter of filters) appendFilter(result, filter);
	return result;
}

function parseTimeoutSetting(value: unknown, settingName: string): number | undefined {
	const timeoutMs = parseHttpIdleTimeoutMs(value);
	if (timeoutMs !== undefined) {
		return timeoutMs;
	}
	if (value !== undefined) {
		throw new Error(`Invalid ${settingName} setting: ${String(value)}`);
	}
	return undefined;
}

/** Stall bounds must be strictly positive — 0 is not "disabled" here (it would stall instantly). */
function parseStallBoundMs(value: unknown, settingName: string): number | undefined {
	if (value === undefined) return undefined;
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
		throw new Error(`Invalid ${settingName} setting: ${String(value)}`);
	}
	return Math.floor(value);
}

function sanitizeIntegerSetting(value: unknown, fallback: number, min: number, max: number): number {
	if (typeof value !== "number" || !Number.isInteger(value)) return fallback;
	if (value < min || value > max) return fallback;
	return value;
}

function sanitizeNumberSetting(value: unknown, fallback: number, min: number, max: number): number {
	if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
	if (value < min || value > max) return fallback;
	return value;
}

type WorkerDelegationDiagnosticReporter = (message: string) => void;

function reportInvalidWorkerDelegationField(
	reportDiagnostic: WorkerDelegationDiagnosticReporter | undefined,
	field: keyof WorkerDelegationSettings,
	expectation: string,
): void {
	reportDiagnostic?.(`workerDelegation.${field} must be ${expectation}; the configured value was ignored`);
}

/** Normalize one precedence layer before merging so malformed overrides cannot widen lower-scope authority. */
function normalizeWorkerDelegationLayer(
	value: unknown,
	reportDiagnostic?: WorkerDelegationDiagnosticReporter,
): WorkerDelegationSettings | undefined {
	if (!isPlainRecord(value)) {
		if (value !== undefined)
			reportDiagnostic?.("workerDelegation must be an object; the configured value was ignored");
		return undefined;
	}
	const normalized: WorkerDelegationSettings = {};
	if (Object.hasOwn(value, "enabled")) {
		if (typeof value.enabled === "boolean") normalized.enabled = value.enabled;
		else reportInvalidWorkerDelegationField(reportDiagnostic, "enabled", "a boolean");
	}
	if (Object.hasOwn(value, "orchestrationProfile")) {
		if (typeof value.orchestrationProfile === "string") {
			const profileId = value.orchestrationProfile.trim();
			if (profileId && profileId.length <= MAX_ORCHESTRATION_IDENTIFIER_LENGTH) {
				normalized.orchestrationProfile = profileId;
			} else {
				reportInvalidWorkerDelegationField(
					reportDiagnostic,
					"orchestrationProfile",
					`a nonempty string of at most ${MAX_ORCHESTRATION_IDENTIFIER_LENGTH} characters`,
				);
			}
		} else {
			reportInvalidWorkerDelegationField(
				reportDiagnostic,
				"orchestrationProfile",
				`a nonempty string of at most ${MAX_ORCHESTRATION_IDENTIFIER_LENGTH} characters`,
			);
		}
	}
	if (Object.hasOwn(value, "maxUsd")) {
		if (
			typeof value.maxUsd === "number" &&
			Number.isFinite(value.maxUsd) &&
			value.maxUsd >= 0 &&
			value.maxUsd <= MAX_WORKER_DELEGATION_MAX_USD
		) {
			normalized.maxUsd = value.maxUsd;
		} else {
			reportInvalidWorkerDelegationField(
				reportDiagnostic,
				"maxUsd",
				`a finite number between 0 and ${MAX_WORKER_DELEGATION_MAX_USD}`,
			);
		}
	}
	if (Object.hasOwn(value, "maxWallClockMs")) {
		if (
			typeof value.maxWallClockMs === "number" &&
			Number.isSafeInteger(value.maxWallClockMs) &&
			value.maxWallClockMs >= 0 &&
			value.maxWallClockMs <= MAX_WORKER_DELEGATION_MAX_WALL_CLOCK_MS
		) {
			normalized.maxWallClockMs = value.maxWallClockMs;
		} else {
			reportInvalidWorkerDelegationField(
				reportDiagnostic,
				"maxWallClockMs",
				`a safe integer between 0 and ${MAX_WORKER_DELEGATION_MAX_WALL_CLOCK_MS}`,
			);
		}
	}
	if (Object.hasOwn(value, "writeEnabled")) {
		if (typeof value.writeEnabled === "boolean") normalized.writeEnabled = value.writeEnabled;
		else reportInvalidWorkerDelegationField(reportDiagnostic, "writeEnabled", "a boolean");
	}
	if (Object.hasOwn(value, "maxConcurrent")) {
		if (
			typeof value.maxConcurrent === "number" &&
			Number.isSafeInteger(value.maxConcurrent) &&
			value.maxConcurrent > 0 &&
			value.maxConcurrent <= MAX_WORKER_DELEGATION_MAX_CONCURRENT
		) {
			normalized.maxConcurrent = value.maxConcurrent;
		} else {
			reportInvalidWorkerDelegationField(
				reportDiagnostic,
				"maxConcurrent",
				`a safe integer between 1 and ${MAX_WORKER_DELEGATION_MAX_CONCURRENT}`,
			);
		}
	}
	return normalized;
}

function mergeWorkerDelegationLayers(...layers: unknown[]): WorkerDelegationSettings | undefined {
	let merged: WorkerDelegationSettings | undefined;
	for (const layer of layers) {
		const normalized = normalizeWorkerDelegationLayer(layer);
		if (!normalized) continue;
		merged = { ...(merged ?? {}), ...normalized };
	}
	return merged;
}

export type ThinkingLevel = "off" | "minimal" | "low" | "medium" | "high" | "xhigh" | "max" | "ultra";

export interface ProfileDefinitionInput {
	name?: string;
	description?: string;
	model?: string;
	thinking?: ThinkingLevel;
	modelRouter?: ModelRouterSettings;
	/**
	 * Situational identity: a system-prompt prefix injected while this profile is active, so a
	 * profile becomes a full "situation" = soul + capabilities + model/thinking, switched atomically.
	 */
	soul?: string;
	resources: ResourceProfileSettings;
}

export type ProfilePersistenceScope = "session" | "directory" | "project" | "global" | "reusable-file";

const VALID_THINKING_LEVELS = ["off", "minimal", "low", "medium", "high", "xhigh", "max", "ultra"] as const;
function isThinkingLevel(value: unknown): value is ThinkingLevel {
	return typeof value === "string" && VALID_THINKING_LEVELS.includes(value as ThinkingLevel);
}

function normalizeProfileFilterResource(value: unknown): ResourceProfileFilterSettings {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		throw new Error("resource profile filter must be an object");
	}
	return normalizeResourceProfileFilter(value as Record<string, unknown>);
}

function normalizeProfileResources(value: unknown): ResourceProfileSettings {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		throw new Error("resources must be an object");
	}
	const input = value as Record<string, unknown>;
	const result: ResourceProfileSettings = {};
	for (const kind of ["extensions", "skills", "prompts", "themes", "agents", "tools"] as const) {
		if (input[kind] === undefined) continue;
		result[kind] = normalizeProfileFilterResource(input[kind]);
	}
	return result;
}

function normalizeModelRouterSettings(value: unknown): ModelRouterSettings | undefined {
	if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
	const input = value as Record<string, unknown>;
	const settings: ModelRouterSettings = {};
	for (const key of ["enabled", "judgeEnabled", "fitnessGate"] as const) {
		if (typeof input[key] === "boolean") settings[key] = input[key];
	}
	for (const key of [
		"cheapModel",
		"mediumModel",
		"expensiveModel",
		"learningModel",
		"judgeModel",
		"executorModel",
	] as const) {
		const candidate = input[key];
		if (typeof candidate !== "string") continue;
		const trimmed = candidate.trim();
		if (trimmed) settings[key] = trimmed;
	}
	for (const key of [
		"cheapThinking",
		"mediumThinking",
		"expensiveThinking",
		"executorThinking",
		"judgeThinking",
	] as const) {
		const candidate = input[key];
		if (isThinkingLevel(candidate)) settings[key] = candidate;
	}
	return Object.keys(settings).length > 0 ? settings : undefined;
}

function parseProfileFileDefinition(content: string, fallbackName?: string): ProfileDefinitionInput {
	const parsed = JSON.parse(content) as Record<string, unknown>;
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
		throw new Error("profile file must contain a JSON object");
	}
	const name = (typeof parsed.name === "string" ? parsed.name.trim() : undefined) || fallbackName;
	if (!name) {
		throw new Error("profile name is required");
	}
	const resourceSection = parsed.resources ?? {};
	return {
		name,
		description: typeof parsed.description === "string" ? parsed.description.trim() || undefined : undefined,
		model: typeof parsed.model === "string" ? parsed.model.trim() || undefined : undefined,
		thinking: isThinkingLevel(parsed.thinking) ? parsed.thinking : undefined,
		modelRouter: normalizeModelRouterSettings(parsed.modelRouter),
		soul: typeof parsed.soul === "string" ? parsed.soul.trim() || undefined : undefined,
		resources: normalizeProfileResources(resourceSection),
	};
}

export type SettingsScope = "global" | "project" | "directoryProfile";
export type SettingsErrorScope = SettingsScope;

export interface SettingsManagerCreateOptions {
	projectTrusted?: boolean;
}

export interface SettingsStorage {
	withLock(scope: SettingsScope, fn: (current: string | undefined) => string | undefined): void;
	getProfilesDir?(): string;
	/** Base directory used to resolve explicit ./ or ../ profile references. */
	getProfileResolutionBaseDir?(): string;
}

export interface SettingsError {
	scope: SettingsErrorScope;
	error: Error;
}

/** In-memory settings generation captured around an atomic runtime reload. */
export interface SettingsReloadSnapshot {
	globalSettings: Settings;
	projectSettings: Settings;
	directoryProfileSettings: Settings;
	runtimeResourceProfiles: string[] | undefined;
	inlineResourceProfileDefinitions: Record<string, ProfileDefinitionInput>;
	discoveredResourceProfileDefinitions: Record<string, ResourceProfileSettings>;
	effectiveSettings: Settings;
	projectTrusted: boolean;
	modifiedFields: Set<keyof Settings>;
	modifiedNestedFields: Map<keyof Settings, Set<string>>;
	modifiedProjectFields: Set<keyof Settings>;
	modifiedProjectNestedFields: Map<keyof Settings, Set<string>>;
	globalSettingsLoadError: Error | null;
	projectSettingsLoadError: Error | null;
	directoryProfileInfo: DirectoryResourceProfileInfo | null;
	errors: SettingsError[];
}

export interface GlobalResourceProfileConfiguration {
	resourceProfiles?: Settings["resourceProfiles"];
	activeResourceProfile?: Settings["activeResourceProfile"];
	activeResourceProfiles?: Settings["activeResourceProfiles"];
	externalResourceRoots?: string[];
	trustedResourceRoots?: string[];
}

export class FileSettingsStorage implements SettingsStorage {
	private profileResolutionBaseDir: string;
	private globalSettingsPath: string;
	private projectSettingsPath: string;
	private directoryProfileInfo: DirectoryResourceProfileInfo;
	private legacyDirectoryProfilePath: string;
	private profilesDir: string;

	constructor(cwd: string, agentDir: string) {
		const resolvedCwd = resolvePath(cwd);
		const resolvedAgentDir = resolvePath(agentDir);
		this.profileResolutionBaseDir = resolvedCwd;
		this.globalSettingsPath = configFile(resolvedAgentDir, "settings.json");
		this.projectSettingsPath = join(resolvedCwd, CONFIG_DIR_NAME, "settings.json");
		this.directoryProfileInfo = getDirectoryResourceProfileInfo(resolvedCwd, resolvedAgentDir);
		this.legacyDirectoryProfilePath = join(
			resolvedAgentDir,
			"resource-profiles",
			this.directoryProfileInfo.hash,
			"settings.json",
		);
		this.profilesDir = getProfilesDir(resolvedAgentDir);
	}

	getDirectoryResourceProfileInfo(): DirectoryResourceProfileInfo {
		return { ...this.directoryProfileInfo };
	}

	getProfilesDir(): string {
		return this.profilesDir;
	}

	getProfileResolutionBaseDir(): string {
		return this.profileResolutionBaseDir;
	}

	readDirectoryResourceProfile(): string | undefined {
		const path = this.directoryProfileInfo.path;
		if (existsSync(path)) return readFileSync(path, "utf-8");
		return existsSync(this.legacyDirectoryProfilePath)
			? readFileSync(this.legacyDirectoryProfilePath, "utf-8")
			: undefined;
	}

	private removeMigratedDirectoryProfile(): void {
		if (!existsSync(this.legacyDirectoryProfilePath)) return;
		try {
			rmSync(this.legacyDirectoryProfilePath, { force: true });
		} catch {
			return;
		}
		for (const directory of [
			dirname(this.legacyDirectoryProfilePath),
			dirname(dirname(this.legacyDirectoryProfilePath)),
		]) {
			try {
				rmdirSync(directory);
			} catch {}
		}
	}

	withLock(scope: SettingsScope, fn: (current: string | undefined) => string | undefined): void {
		const path =
			scope === "global"
				? this.globalSettingsPath
				: scope === "project"
					? this.projectSettingsPath
					: this.directoryProfileInfo.path;
		const dir = dirname(path);
		const legacyPath = scope === "directoryProfile" ? this.legacyDirectoryProfilePath : undefined;

		const releases: Array<() => void> = [];
		let migratedLegacy = false;
		try {
			const legacyExists = legacyPath !== undefined && existsSync(legacyPath);
			const canonicalExists = existsSync(path);
			let currentPath = canonicalExists ? path : legacyExists ? (legacyPath ?? path) : path;
			const fileExists = canonicalExists || legacyExists;

			// Directory-profile locks always follow legacy -> canonical order, including a partial migration
			// where both files exist. This keeps old and new SDK clients from deadlocking each other.
			if (legacyPath && legacyExists) {
				releases.push(acquireFileLockSync(legacyPath, LOW_LATENCY_FILE_LOCK_OPTIONS));
			}
			if (canonicalExists) releases.push(acquireFileLockSync(path, LOW_LATENCY_FILE_LOCK_OPTIONS));

			// A legacy directory overlay can race another process that already knows the canonical path.
			// Hold the legacy lock, then bind to the canonical lock before deriving the update.
			if (legacyExists && !canonicalExists) {
				if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
				releases.push(acquireFileLockSync(path, LOW_LATENCY_FILE_LOCK_OPTIONS));
				if (existsSync(path)) currentPath = path;
			}

			const current = fileExists ? readFileSync(currentPath, "utf-8") : undefined;
			const next = fn(current);
			if (next !== undefined) {
				// Only create directory when we actually need to write
				if (!existsSync(dir)) {
					mkdirSync(dir, { recursive: true });
				}
				if (releases.length === 0) {
					releases.push(acquireFileLockSync(path, LOW_LATENCY_FILE_LOCK_OPTIONS));
				}
				writeFileAtomicSync(path, next);
				migratedLegacy = legacyExists;
			}
		} finally {
			for (const release of releases.reverse()) release();
			if (migratedLegacy) this.removeMigratedDirectoryProfile();
		}
	}
}

export class InMemorySettingsStorage implements SettingsStorage {
	private global: string | undefined;
	private project: string | undefined;
	private directoryProfile: string | undefined;

	withLock(scope: SettingsScope, fn: (current: string | undefined) => string | undefined): void {
		const current = scope === "global" ? this.global : scope === "project" ? this.project : this.directoryProfile;
		const next = fn(current);
		if (next === undefined) {
			return;
		}
		if (scope === "global") {
			this.global = next;
			return;
		}
		if (scope === "project") {
			this.project = next;
			return;
		}
		this.directoryProfile = next;
	}
}

export class SettingsManager {
	private storage: SettingsStorage;
	private globalSettings: Settings;
	private projectSettings: Settings;
	private directoryProfileSettings: Settings;
	private runtimeResourceProfiles: string[] | undefined;
	private inlineResourceProfileDefinitions: Record<string, ProfileDefinitionInput> = {};
	private discoveredResourceProfileDefinitions: Record<string, ResourceProfileSettings> = {};
	settings: Settings;
	private projectTrusted: boolean;
	private modifiedFields = new Set<keyof Settings>(); // Track global fields modified during session
	private modifiedNestedFields = new Map<keyof Settings, Set<string>>(); // Track global nested field modifications
	private modifiedProjectFields = new Set<keyof Settings>(); // Track project fields modified during session
	private modifiedProjectNestedFields = new Map<keyof Settings, Set<string>>(); // Track project nested field modifications
	private globalSettingsLoadError: Error | null = null; // Track if global settings file had parse errors
	private projectSettingsLoadError: Error | null = null; // Track if project settings file had parse errors
	private directoryProfileInfo: DirectoryResourceProfileInfo | null = null;
	private profileRegistry!: ProfileRegistry;
	private writeQueue: Promise<void> = Promise.resolve();
	private errors: SettingsError[];
	private readonly changeListeners = new Set<() => void>();

	private constructor(
		storage: SettingsStorage,
		initialGlobal: Settings,
		initialProject: Settings,
		initialDirectoryProfile: Settings = {},
		globalLoadError: Error | null = null,
		projectLoadError: Error | null = null,
		initialErrors: SettingsError[] = [],
		projectTrusted = true,
		directoryProfileInfo: DirectoryResourceProfileInfo | null = null,
	) {
		this.storage = storage;
		this.globalSettings = initialGlobal;
		this.projectSettings = initialProject;
		this.directoryProfileSettings = initialDirectoryProfile;
		this.projectTrusted = projectTrusted;
		this.globalSettingsLoadError = globalLoadError;
		this.projectSettingsLoadError = projectLoadError;
		this.directoryProfileInfo = directoryProfileInfo;
		this.errors = [...initialErrors];
		this.reportWorkerDelegationDiagnostics("global", this.globalSettings);
		this.reportWorkerDelegationDiagnostics("project", this.projectSettings);
		this.reportWorkerDelegationDiagnostics("directoryProfile", this.directoryProfileSettings);
		this.settings = this.mergeEffectiveSettings();
		this.refreshProfileRegistry();
	}

	private createProfileRegistry(): ProfileRegistry {
		return new ProfileRegistry({
			globalSettings: this.globalSettings,
			projectSettings: this.projectSettings,
			directoryProfileSettings: this.directoryProfileSettings,
			inlineResourceProfileDefinitions: this.inlineResourceProfileDefinitions,
			discoveredResourceProfileDefinitions: this.discoveredResourceProfileDefinitions,
			profilesDir: this.storage.getProfilesDir?.(),
			externalResourceRoots: this.getEffectiveExternalResourceRoots(),
		});
	}

	private profileDiagnosticKeys = new Set<string>();
	private reportProfileDiagnostic(scope: SettingsErrorScope, message: string): void {
		const key = `${scope}:${message}`;
		if (this.profileDiagnosticKeys.has(key)) {
			return;
		}
		this.profileDiagnosticKeys.add(key);
		this.errors.push({ scope, error: new Error(message) });
	}

	private reportWorkerDelegationDiagnostics(scope: SettingsErrorScope, settings: Settings): void {
		const diagnostics: string[] = [];
		normalizeWorkerDelegationLayer(settings.workerDelegation, (message) => diagnostics.push(message));
		const pinPolicy = compileWorkerModelPinPolicy({ [scope]: settings.workerDelegation?.modelPins });
		if (pinPolicy.status === "invalid") diagnostics.push(...pinPolicy.diagnostics);
		if (diagnostics.length > 0) {
			this.recordError(scope, new Error(`Worker delegation settings: ${diagnostics.join("; ")}`));
		}
	}

	private getActiveProfileNamesForDiagnostics(): string[] {
		// Mirror getActiveResourceProfileNames()'s source precedence (runtime profiles from
		// --resource-profile take priority) so a bad runtime profile name still surfaces a
		// "profile not found" diagnostic instead of silently applying zero filtering.
		if (this.runtimeResourceProfiles !== undefined) {
			return normalizeResourceProfileNames(this.runtimeResourceProfiles);
		}
		// `/profiles none` is persisted globally as an explicit empty array. It is a durable
		// user-level off switch and must dominate project/directory/default/external fallbacks until
		// the user explicitly selects another profile.
		if (hasExplicitEmptyActiveResourceProfileSelection(this.globalSettings)) {
			return [];
		}
		const explicitProfiles =
			this.settings.activeResourceProfiles && this.settings.activeResourceProfiles.length > 0
				? this.settings.activeResourceProfiles
				: this.settings.activeResourceProfile
					? [this.settings.activeResourceProfile]
					: [];
		const names = normalizeResourceProfileNames(explicitProfiles);
		return names.length > 0 || hasExplicitActiveResourceProfileSelection(this.settings)
			? names
			: this.getExternalRootActiveResourceProfileNames();
	}

	private getExternalRootActiveResourceProfileNames(): string[] {
		const names: string[] = [];
		for (const root of this.getEffectiveExternalResourceRoots()) {
			try {
				const settingsPath = join(root, "settings.json");
				if (!existsSync(settingsPath)) continue;
				const parsed = JSON.parse(readFileSync(settingsPath, "utf-8")) as Settings;
				names.push(...normalizeActiveResourceProfiles(parsed));
			} catch {
				// External-root settings are optional; ignore malformed files for active-profile fallback.
			}
		}
		return [...new Set(names)];
	}

	private refreshProfileRegistry(): void {
		this.profileDiagnosticKeys.clear();
		this.profileRegistry = this.createProfileRegistry();
		const registryDiagnostics = this.profileRegistry.listDiagnostics();
		for (const diagnostic of registryDiagnostics) {
			const path = diagnostic.path ? ` (${diagnostic.path})` : "";
			this.reportProfileDiagnostic("global", `Profile diagnostic${path}: ${diagnostic.message}`);
		}
		for (const profileName of this.getActiveProfileNamesForDiagnostics()) {
			if (!this.resolveProfileFromRegistry(this.profileRegistry, profileName)) {
				this.reportProfileDiagnostic("global", `Active profile not found: ${profileName}`);
			}
		}
	}

	private resolveProfileFromRegistry(registry: ProfileRegistry, profileRef: string) {
		return profileRef.startsWith("./") || profileRef.startsWith("../")
			? registry.resolveProfileRef(profileRef, this.storage.getProfileResolutionBaseDir?.() ?? process.cwd())
			: registry.getProfile(profileRef);
	}

	private mergeEffectiveSettings(): Settings {
		let merged = deepMergeSettings(this.globalSettings, this.projectSettings);
		merged = deepMergeSettings(merged, this.directoryProfileSettings);
		const workerDelegation = mergeWorkerDelegationLayers(
			this.globalSettings.workerDelegation,
			this.projectSettings.workerDelegation,
			this.directoryProfileSettings.workerDelegation,
		);
		if (workerDelegation) merged.workerDelegation = workerDelegation;
		else delete merged.workerDelegation;
		if (this.runtimeResourceProfiles !== undefined) {
			merged = deepMergeSettings(merged, {
				activeResourceProfile: this.runtimeResourceProfiles,
				activeResourceProfiles: this.runtimeResourceProfiles,
			});
		}
		return merged;
	}

	private recomputeSettings(): void {
		this.settings = this.mergeEffectiveSettings();
		this.refreshProfileRegistry();
		this.notifyChanges();
	}

	/** Subscribe to synchronous effective-settings transitions. Listener failures never break persistence. */
	subscribeChanges(listener: () => void): () => void {
		this.changeListeners.add(listener);
		return () => this.changeListeners.delete(listener);
	}

	private notifyChanges(): void {
		for (const listener of this.changeListeners) {
			try {
				listener();
			} catch {
				// Settings persistence is authoritative; a runtime projection can recover on its next refresh.
			}
		}
	}

	/** Create a SettingsManager that loads from files */
	static create(
		cwd: string,
		agentDir: string = getAgentDir(),
		options: SettingsManagerCreateOptions = {},
	): SettingsManager {
		const storage = new FileSettingsStorage(cwd, agentDir);
		return SettingsManager.fromStorage(storage, options);
	}

	/** Create a SettingsManager from an arbitrary storage backend */
	static fromStorage(storage: SettingsStorage, options: SettingsManagerCreateOptions = {}): SettingsManager {
		const projectTrusted = options.projectTrusted ?? true;
		const globalLoad = SettingsManager.tryLoadFromStorage(storage, "global");
		const projectLoad = SettingsManager.tryLoadFromStorage(storage, "project", projectTrusted);
		const directoryProfileLoad = SettingsManager.tryLoadDirectoryProfileFromStorage(storage);
		const initialErrors: SettingsError[] = [];
		if (globalLoad.error) {
			initialErrors.push({ scope: "global", error: globalLoad.error });
		}
		if (projectLoad.error) {
			initialErrors.push({ scope: "project", error: projectLoad.error });
		}
		if (directoryProfileLoad.error) {
			initialErrors.push({ scope: "directoryProfile", error: directoryProfileLoad.error });
		}

		return new SettingsManager(
			storage,
			globalLoad.settings,
			projectLoad.settings,
			directoryProfileLoad.settings,
			globalLoad.error,
			projectLoad.error,
			initialErrors,
			projectTrusted,
			directoryProfileLoad.info,
		);
	}

	/** Create an in-memory SettingsManager (no file I/O) */
	static inMemory(settings: Partial<Settings> = {}): SettingsManager {
		const storage = new InMemorySettingsStorage();
		const initialSettings = SettingsManager.migrateSettings(structuredClone(settings) as Record<string, unknown>);
		storage.withLock("global", () => JSON.stringify(initialSettings, null, 2));
		return SettingsManager.fromStorage(storage);
	}

	private static loadFromStorage(storage: SettingsStorage, scope: SettingsScope, projectTrusted = true): Settings {
		if (scope === "project" && !projectTrusted) {
			return {};
		}

		let content: string | undefined;
		storage.withLock(scope, (current) => {
			content = current;
			return undefined;
		});

		if (!content) {
			return {};
		}
		const settings = JSON.parse(content);
		return SettingsManager.migrateSettings(settings);
	}

	private static tryLoadFromStorage(
		storage: SettingsStorage,
		scope: SettingsScope,
		projectTrusted = true,
	): { settings: Settings; error: Error | null } {
		try {
			return { settings: SettingsManager.loadFromStorage(storage, scope, projectTrusted), error: null };
		} catch (error) {
			return { settings: {}, error: error as Error };
		}
	}

	private static tryLoadDirectoryProfileFromStorage(storage: SettingsStorage): {
		settings: Settings;
		error: Error | null;
		info: DirectoryResourceProfileInfo | null;
	} {
		const info = storage instanceof FileSettingsStorage ? storage.getDirectoryResourceProfileInfo() : null;
		try {
			let content: string | undefined;
			if (storage instanceof FileSettingsStorage) {
				content = storage.readDirectoryResourceProfile();
			} else {
				storage.withLock("directoryProfile", (current) => {
					content = current;
					return undefined;
				});
			}
			if (!content) return { settings: {}, error: null, info };
			const settings = JSON.parse(content);
			return { settings: SettingsManager.migrateSettings(settings), error: null, info };
		} catch (error) {
			return { settings: {}, error: error as Error, info };
		}
	}

	/** Migrate old settings format to new format */
	private static migrateSettings(settings: Record<string, unknown>): Settings {
		// Migrate queueMode -> steeringMode
		if ("queueMode" in settings && !("steeringMode" in settings)) {
			settings.steeringMode = settings.queueMode;
			delete settings.queueMode;
		}

		// Migrate legacy websockets boolean -> transport enum
		if (!("transport" in settings) && typeof settings.websockets === "boolean") {
			settings.transport = settings.websockets ? "websocket" : "sse";
			delete settings.websockets;
		}

		// Migrate old skills object format to new array format
		if (
			"skills" in settings &&
			typeof settings.skills === "object" &&
			settings.skills !== null &&
			!Array.isArray(settings.skills)
		) {
			const skillsSettings = settings.skills as {
				enableSkillCommands?: boolean;
				customDirectories?: unknown;
			};
			if (skillsSettings.enableSkillCommands !== undefined && settings.enableSkillCommands === undefined) {
				settings.enableSkillCommands = skillsSettings.enableSkillCommands;
			}
			if (Array.isArray(skillsSettings.customDirectories) && skillsSettings.customDirectories.length > 0) {
				settings.skills = skillsSettings.customDirectories;
			} else {
				delete settings.skills;
			}
		}

		// Migrate retry.maxDelayMs -> retry.provider.maxRetryDelayMs
		if (
			"retry" in settings &&
			typeof settings.retry === "object" &&
			settings.retry !== null &&
			!Array.isArray(settings.retry)
		) {
			const retrySettings = settings.retry as Record<string, unknown>;
			const providerSettings =
				typeof retrySettings.provider === "object" && retrySettings.provider !== null
					? (retrySettings.provider as Record<string, unknown>)
					: undefined;
			if (
				typeof retrySettings.maxDelayMs === "number" &&
				(providerSettings?.maxRetryDelayMs === undefined || providerSettings?.maxRetryDelayMs === null)
			) {
				retrySettings.provider = {
					...(providerSettings ?? {}),
					maxRetryDelayMs: retrySettings.maxDelayMs,
				};
			}
			delete retrySettings.maxDelayMs;
		}

		return settings as Settings;
	}

	getGlobalSettings(): Settings {
		return structuredClone(this.globalSettings);
	}

	getProjectSettings(): Settings {
		return structuredClone(this.projectSettings);
	}

	getDirectoryResourceProfileSettings(): Settings {
		return structuredClone(this.directoryProfileSettings);
	}

	getDirectoryResourceProfileInfo(): DirectoryResourceProfileInfo | null {
		return this.directoryProfileInfo ? { ...this.directoryProfileInfo } : null;
	}

	getProfileRegistry(): ProfileRegistry {
		this.refreshProfileRegistry();
		return this.profileRegistry;
	}

	getActiveResourceProfileNames(): string[] {
		if (this.runtimeResourceProfiles !== undefined) {
			return [...this.runtimeResourceProfiles];
		}
		if (hasExplicitEmptyActiveResourceProfileSelection(this.globalSettings)) {
			return [];
		}
		const names = normalizeActiveResourceProfiles(this.settings);
		return names.length > 0 || hasExplicitActiveResourceProfileSelection(this.settings)
			? names
			: this.getExternalRootActiveResourceProfileNames();
	}

	hasExplicitActiveResourceProfileSelection(): boolean {
		// An explicit empty runtime/settings selection means "no profile", not an invalid profile
		// whose authority should collapse to deny-all. Non-empty unresolved refs remain explicit and
		// are diagnosed/strictly denied through the normal active-profile path.
		return this.getActiveResourceProfileNames().length > 0;
	}

	/**
	 * Aggregate ONLY the active profiles' contribution to a resource kind's filter — the user's own
	 * legacy `disabledResources` list is not merged in. Includes the strict-UAC deny-all (an
	 * authority-bearing kind no active profile mentions is denied outright); that denial is
	 * profile-driven too. Shared by `getResourceProfileFilter` (which merges the legacy disable list
	 * on top) and `isResourceDeniedByActiveProfile` (which must attribute a denial to the profile
	 * ALONE — a user-only disable must never be reported as "withheld by the active resource
	 * profile").
	 */
	private computeProfileOnlyResourceFilter(kind: ResourceProfileKind): Required<ResourceProfileFilterSettings> {
		const profileFilter: ResourceProfileFilterSettings = {};
		const seenProfiles = new Set<string>();
		const registry = this.getProfileRegistry();
		const activeProfileNames = this.getActiveResourceProfileNames();
		let kindMentionedByProfile = false;
		for (const profileName of activeProfileNames) {
			if (seenProfiles.has(profileName)) continue;
			seenProfiles.add(profileName);
			const kindFilter = this.resolveProfileFromRegistry(registry, profileName)?.resources[kind];
			if (kindFilter && ((kindFilter.allow?.length ?? 0) > 0 || (kindFilter.block?.length ?? 0) > 0)) {
				kindMentionedByProfile = true;
			}
			appendFilter(profileFilter, kindFilter);
		}

		// Strict UAC: an active profile set is the COMPLETE grant. An authority-bearing kind that no
		// active profile explicitly mentions is denied outright — grant-all must be said out loud
		// via `allow: ["*"]`. Themes are exempt (cosmetic, no authority). With no active profile,
		// behavior is unchanged: profiles are the opt-in least-privilege boundary.
		if (activeProfileNames.length > 0 && kind !== "themes" && !kindMentionedByProfile) {
			return { allow: [], block: ["*"] };
		}

		return {
			allow: [...new Set(profileFilter.allow ?? [])],
			block: [...new Set(profileFilter.block ?? [])],
		};
	}

	getResourceProfileFilter(kind: ResourceProfileKind): Required<ResourceProfileFilterSettings> {
		const legacyFilter = mergeResourceProfileFilters(
			collectLegacyDisabledFilterFromSettings(this.globalSettings, kind),
			collectLegacyDisabledFilterFromSettings(this.projectSettings, kind),
			collectLegacyDisabledFilterFromSettings(this.directoryProfileSettings, kind),
		);
		const filter = mergeResourceProfileFilters(legacyFilter, this.computeProfileOnlyResourceFilter(kind));
		return {
			allow: [...new Set(filter.allow ?? [])],
			block: [...new Set(filter.block ?? [])],
		};
	}

	/**
	 * Explicit off-switch for passive default-on resources. Unlike the strict profile grant,
	 * an omitted allow entry is not a disable: only disabledResources, the resource selector's
	 * negative filters, or an authored profile block wins. This keeps default-on cosmetic resources
	 * available without weakening the import boundary for ordinary authority-bearing extensions.
	 */
	isResourceExplicitlyDisabled(kind: ResourceProfileKind, resourcePath: string, baseDir = ""): boolean {
		const userDisabled = mergeResourceProfileFilters(
			collectLegacyDisabledFilterFromSettings(this.globalSettings, kind),
			collectLegacyDisabledFilterFromSettings(this.projectSettings, kind),
			collectLegacyDisabledFilterFromSettings(this.directoryProfileSettings, kind),
		).block;
		if (matchesResourceProfilePattern(resourcePath, userDisabled ?? [], baseDir)) return true;
		if (isResourceDisabledByTopLevelOverrides(this.settings, kind, resourcePath, baseDir)) {
			return true;
		}

		const registry = this.getProfileRegistry();
		const seen = new Set<string>();
		for (const profileName of this.getActiveResourceProfileNames()) {
			if (seen.has(profileName)) continue;
			seen.add(profileName);
			const explicitBlocks = this.resolveProfileFromRegistry(registry, profileName)?.resources[kind]?.block ?? [];
			if (matchesResourceProfilePattern(resourcePath, explicitBlocks, baseDir)) return true;
		}
		return false;
	}

	/**
	 * Profile grants the user's own disable list overrides. RATIFIED precedence: a user disable
	 * (`disabledResources` / `!` overrides) is a hard off-switch that always WINS over a profile
	 * allow (the legacy disabled filter merges into every profile filter as a block, and blocks
	 * beat allows). This helper only SURFACES the conflict so a granted-but-disabled resource
	 * doesn't look like a broken grant.
	 */
	getProfileGrantsOverriddenByUserDisable(kind: ResourceProfileKind): string[] {
		const disabled = this.settings.disabledResources?.[kind] ?? [];
		if (!Array.isArray(disabled) || disabled.length === 0) return [];
		const registry = this.getProfileRegistry();
		const conflicts = new Set<string>();
		for (const profileName of this.getActiveResourceProfileNames()) {
			const filter = this.resolveProfileFromRegistry(registry, profileName)?.resources[kind];
			for (const allowEntry of filter?.allow ?? []) {
				if (allowEntry === "*") continue;
				if (disabled.includes(allowEntry) || matchesResourceProfilePattern(allowEntry, disabled)) {
					conflicts.add(allowEntry);
				}
			}
		}
		return [...conflicts];
	}

	isResourceAllowedByProfile(kind: ResourceProfileKind, resourcePath: string, baseDir = ""): boolean {
		const filter = this.getResourceProfileFilter(kind);
		if (filter.allow.length > 0 && !matchesResourceProfilePattern(resourcePath, filter.allow, baseDir)) {
			return false;
		}
		if (matchesResourceProfilePattern(resourcePath, filter.block, baseDir)) {
			return false;
		}
		return true;
	}

	/**
	 * Whether the ACTIVE PROFILE alone denies this resource — the user's own legacy
	 * `disabledResources` list is ignored. A "withheld by the active resource profile" report must
	 * use this, not `isResourceAllowedByProfile`: that check merges the user's own disables in, so a
	 * plain user-disabled resource (no profile even mentioning it) would otherwise be misattributed
	 * to the profile.
	 */
	isResourceDeniedByActiveProfile(kind: ResourceProfileKind, resourcePath: string, baseDir = ""): boolean {
		if (this.getActiveResourceProfileNames().length === 0) return false;
		const filter = this.computeProfileOnlyResourceFilter(kind);
		if (filter.allow.length > 0 && !matchesResourceProfilePattern(resourcePath, filter.allow, baseDir)) {
			return true;
		}
		return matchesResourceProfilePattern(resourcePath, filter.block, baseDir);
	}

	/**
	 * Situational soul(s) of the currently active profile(s): a system-prompt identity prefix
	 * injected while the profile is active. Multiple active profiles' souls are concatenated.
	 */
	getActiveProfileSoul(): string | undefined {
		// First-wins precedence (like profile model/thinking): the most-specific active profile's soul
		// is the identity — concatenating multiple souls would inject contradictory identities.
		const registry = this.getProfileRegistry();
		const seen = new Set<string>();
		for (const profileName of this.getActiveResourceProfileNames()) {
			if (seen.has(profileName)) continue;
			seen.add(profileName);
			const soul = this.resolveProfileFromRegistry(registry, profileName)?.soul?.trim();
			if (soul) return soul;
		}
		return undefined;
	}

	isProjectTrusted(): boolean {
		return this.projectTrusted;
	}

	setProjectTrusted(trusted: boolean): void {
		if (this.projectTrusted === trusted) {
			return;
		}

		this.projectTrusted = trusted;
		this.modifiedProjectFields.clear();
		this.modifiedProjectNestedFields.clear();

		if (!trusted) {
			this.projectSettings = {};
			this.projectSettingsLoadError = null;
			this.recomputeSettings();
			return;
		}

		const projectLoad = SettingsManager.tryLoadFromStorage(this.storage, "project", trusted);
		this.projectSettings = projectLoad.settings;
		this.projectSettingsLoadError = projectLoad.error;
		if (projectLoad.error) {
			this.recordError("project", projectLoad.error);
		} else {
			this.reportWorkerDelegationDiagnostics("project", this.projectSettings);
		}
		this.recomputeSettings();
	}

	async reload(): Promise<void> {
		await this.writeQueue;
		const globalLoad = SettingsManager.tryLoadFromStorage(this.storage, "global");
		if (!globalLoad.error) {
			this.globalSettings = globalLoad.settings;
			this.globalSettingsLoadError = null;
			this.reportWorkerDelegationDiagnostics("global", this.globalSettings);
		} else {
			this.globalSettingsLoadError = globalLoad.error;
			this.recordError("global", globalLoad.error);
		}

		this.modifiedFields.clear();
		this.modifiedNestedFields.clear();
		this.modifiedProjectFields.clear();
		this.modifiedProjectNestedFields.clear();

		const projectLoad = SettingsManager.tryLoadFromStorage(this.storage, "project", this.projectTrusted);
		if (!projectLoad.error) {
			this.projectSettings = projectLoad.settings;
			this.projectSettingsLoadError = null;
			this.reportWorkerDelegationDiagnostics("project", this.projectSettings);
		} else {
			this.projectSettingsLoadError = projectLoad.error;
			this.recordError("project", projectLoad.error);
		}

		const directoryProfileLoad = SettingsManager.tryLoadDirectoryProfileFromStorage(this.storage);
		this.directoryProfileInfo = directoryProfileLoad.info;
		if (!directoryProfileLoad.error) {
			this.directoryProfileSettings = directoryProfileLoad.settings;
			this.reportWorkerDelegationDiagnostics("directoryProfile", this.directoryProfileSettings);
		} else {
			this.recordError("directoryProfile", directoryProfileLoad.error);
		}

		this.recomputeSettings();
	}

	/** Capture the complete in-memory settings generation before runtime reload mutates it. */
	createReloadSnapshot(): SettingsReloadSnapshot {
		return {
			globalSettings: structuredClone(this.globalSettings),
			projectSettings: structuredClone(this.projectSettings),
			directoryProfileSettings: structuredClone(this.directoryProfileSettings),
			runtimeResourceProfiles:
				this.runtimeResourceProfiles === undefined ? undefined : [...this.runtimeResourceProfiles],
			inlineResourceProfileDefinitions: structuredClone(this.inlineResourceProfileDefinitions),
			discoveredResourceProfileDefinitions: structuredClone(this.discoveredResourceProfileDefinitions),
			effectiveSettings: structuredClone(this.settings),
			projectTrusted: this.projectTrusted,
			modifiedFields: new Set(this.modifiedFields),
			modifiedNestedFields: this.cloneModifiedNestedFields(this.modifiedNestedFields),
			modifiedProjectFields: new Set(this.modifiedProjectFields),
			modifiedProjectNestedFields: this.cloneModifiedNestedFields(this.modifiedProjectNestedFields),
			globalSettingsLoadError: this.globalSettingsLoadError,
			projectSettingsLoadError: this.projectSettingsLoadError,
			directoryProfileInfo: this.directoryProfileInfo ? { ...this.directoryProfileInfo } : null,
			errors: [...this.errors],
		};
	}

	/** Restore a failed runtime reload without changing the on-disk settings generation. */
	restoreReloadSnapshot(snapshot: SettingsReloadSnapshot): void {
		this.globalSettings = structuredClone(snapshot.globalSettings);
		this.projectSettings = structuredClone(snapshot.projectSettings);
		this.directoryProfileSettings = structuredClone(snapshot.directoryProfileSettings);
		this.runtimeResourceProfiles =
			snapshot.runtimeResourceProfiles === undefined ? undefined : [...snapshot.runtimeResourceProfiles];
		this.inlineResourceProfileDefinitions = structuredClone(snapshot.inlineResourceProfileDefinitions);
		this.discoveredResourceProfileDefinitions = structuredClone(snapshot.discoveredResourceProfileDefinitions);
		this.settings = structuredClone(snapshot.effectiveSettings);
		this.projectTrusted = snapshot.projectTrusted;
		this.modifiedFields = new Set(snapshot.modifiedFields);
		this.modifiedNestedFields = this.cloneModifiedNestedFields(snapshot.modifiedNestedFields);
		this.modifiedProjectFields = new Set(snapshot.modifiedProjectFields);
		this.modifiedProjectNestedFields = this.cloneModifiedNestedFields(snapshot.modifiedProjectNestedFields);
		this.globalSettingsLoadError = snapshot.globalSettingsLoadError;
		this.projectSettingsLoadError = snapshot.projectSettingsLoadError;
		this.directoryProfileInfo = snapshot.directoryProfileInfo ? { ...snapshot.directoryProfileInfo } : null;
		this.errors = [...snapshot.errors];
		this.refreshProfileRegistry();
		this.notifyChanges();
	}

	/** Apply additional overrides on top of current settings */
	applyOverrides(overrides: Partial<Settings>): void {
		const normalizedOverrides = { ...overrides };
		if (Object.hasOwn(overrides, "workerDelegation")) {
			const workerDelegation = normalizeWorkerDelegationLayer(overrides.workerDelegation);
			if (workerDelegation) normalizedOverrides.workerDelegation = workerDelegation;
			else delete normalizedOverrides.workerDelegation;
		}
		this.settings = deepMergeSettings(this.settings, normalizedOverrides);
		this.notifyChanges();
	}

	/** Select runtime-only resource profiles, e.g. from CLI/subagent launch options. */
	setRuntimeResourceProfiles(profileNames: string[]): void {
		this.runtimeResourceProfiles = normalizeResourceProfileNames(profileNames);
		this.recomputeSettings();
	}

	/** Add one-shot profile definitions from CLI/SDK/ephemeral agent launch input. Never writes to disk. */
	addInlineResourceProfileDefinitions(
		profiles: Record<string, ResourceProfileSettings | ProfileDefinitionInput>,
	): void {
		const next = { ...this.inlineResourceProfileDefinitions };
		for (const [name, input] of Object.entries(profiles)) {
			const existing = next[name];
			const definition = Object.hasOwn(input, "resources")
				? (input as ProfileDefinitionInput)
				: { resources: input as ResourceProfileSettings };
			const resources = mergeResourceProfileSettings(existing?.resources, definition.resources);
			next[name] = this.mergeProfileDefinition(name, definition, resources, existing);
		}
		this.inlineResourceProfileDefinitions = next;
		this.refreshProfileRegistry();
	}

	/** Replace profile definitions discovered inside loaded resource files. Never writes to disk. */
	replaceDiscoveredResourceProfileDefinitions(profiles: Record<string, ResourceProfileSettings>): void {
		this.discoveredResourceProfileDefinitions = { ...profiles };
		this.refreshProfileRegistry();
	}

	/** Add profile definitions discovered after resource resolution, e.g. context agent files. Never writes to disk. */
	addDiscoveredResourceProfileDefinitions(profiles: Record<string, ResourceProfileSettings>): void {
		this.discoveredResourceProfileDefinitions = mergeResourceProfileMap(
			this.discoveredResourceProfileDefinitions,
			profiles,
		);
		this.refreshProfileRegistry();
	}

	private normalizeProfileName(profileName: string): string {
		const trimmed = profileName.trim();
		if (!trimmed) {
			throw new Error("Profile name is required");
		}
		const errors = validateSkillName(trimmed);
		if (errors.length > 0) {
			throw new Error(`Invalid profile name "${trimmed}": ${errors.join(", ")}`);
		}
		return trimmed;
	}

	private normalizeProfileSelection(profileName: string): string {
		const trimmed = profileName.trim();
		if (trimmed.startsWith("./") || trimmed.startsWith("../")) {
			return trimmed;
		}
		return this.normalizeProfileName(trimmed);
	}

	private sanitizeProfileResources(resources: ResourceProfileSettings): ResourceProfileSettings {
		const result: ResourceProfileSettings = {};
		for (const kind of ["extensions", "skills", "prompts", "themes", "agents", "tools"] as const) {
			const filter = resources[kind];
			if (!filter) {
				continue;
			}
			result[kind] = {
				allow: filter.allow ? [...filter.allow] : undefined,
				block: filter.block ? [...filter.block] : undefined,
			};
		}
		return result;
	}

	private decodeStoredProfileDefinition(
		name: string,
		stored: ResourceProfileSettings | ProfileDefinitionInput | undefined,
	): ProfileDefinitionInput {
		if (stored && Object.hasOwn(stored, "resources")) {
			const definition = stored as ProfileDefinitionInput;
			return { ...definition, name, resources: this.sanitizeProfileResources(definition.resources) };
		}
		return {
			name,
			resources: this.sanitizeProfileResources((stored as ResourceProfileSettings | undefined) ?? {}),
		};
	}

	private mergeProfileDefinition(
		name: string,
		definition: ProfileDefinitionInput,
		resources: ResourceProfileSettings,
		existing?: ResourceProfileSettings | ProfileDefinitionInput,
	): ProfileDefinitionInput {
		const previous = this.decodeStoredProfileDefinition(name, existing);
		return {
			...previous,
			name,
			resources,
			description: definition.description ?? previous.description,
			model: Object.hasOwn(definition, "model") ? definition.model : previous.model,
			thinking: definition.thinking ?? previous.thinking,
			modelRouter: definition.modelRouter ?? previous.modelRouter,
			soul: definition.soul ?? previous.soul,
		};
	}

	private encodeStoredProfileDefinition(
		definition: ProfileDefinitionInput,
	): ResourceProfileSettings | ProfileDefinitionInput | undefined {
		const hasMetadata = Boolean(
			definition.description || definition.model || definition.thinking || definition.modelRouter || definition.soul,
		);
		if (hasMetadata) return definition;
		return Object.keys(definition.resources).length > 0 ? definition.resources : undefined;
	}

	private updateStoredProfileDefinition(
		settings: Settings,
		name: string,
		definition: ProfileDefinitionInput,
		resources: ResourceProfileSettings,
	): void {
		const definitions = { ...(settings.resourceProfiles ?? {}) };
		const stored = this.encodeStoredProfileDefinition(
			this.mergeProfileDefinition(name, definition, resources, definitions[name]),
		);
		if (stored) definitions[name] = stored;
		else delete definitions[name];
		if (Object.keys(definitions).length > 0) settings.resourceProfiles = definitions;
		else delete settings.resourceProfiles;
	}

	private renameStoredProfileDefinition(settings: Settings, oldName: string, newName: string): void {
		const definitions = { ...(settings.resourceProfiles ?? {}) };
		if (!definitions[oldName]) throw new Error(`Profile not found: ${oldName}`);
		if (definitions[newName]) throw new Error(`Profile already exists: ${newName}`);
		definitions[newName] = definitions[oldName];
		delete definitions[oldName];
		settings.resourceProfiles = definitions;
		if (settings.activeResourceProfile === oldName) settings.activeResourceProfile = newName;
		if (settings.activeResourceProfiles) {
			settings.activeResourceProfiles = settings.activeResourceProfiles.map((name) =>
				name === oldName ? newName : name,
			);
		}
	}

	private setActiveProfileInSettings(settings: Settings, profileName: string | undefined): void {
		if (profileName) {
			settings.activeResourceProfile = profileName;
			settings.activeResourceProfiles = [profileName];
			return;
		}
		settings.activeResourceProfiles = [];
		delete settings.activeResourceProfile;
	}

	private rewriteActiveProfileReference(settings: Settings, oldName: string, newName: string | undefined): boolean {
		let changed = false;
		const rewrite = (values: string[]): string[] => {
			const next = values.flatMap((value) => {
				if (value !== oldName) return [value];
				changed = true;
				return newName ? [newName] : [];
			});
			return [...new Set(next)];
		};

		if (settings.activeResourceProfiles !== undefined) {
			const next = rewrite(normalizeResourceProfileNames(settings.activeResourceProfiles));
			if (next.length > 0) settings.activeResourceProfiles = next;
			else delete settings.activeResourceProfiles;
		}
		if (settings.activeResourceProfile !== undefined) {
			const next = rewrite(normalizeResourceProfileNames(settings.activeResourceProfile));
			if (next.length === 0) delete settings.activeResourceProfile;
			else
				settings.activeResourceProfile =
					Array.isArray(settings.activeResourceProfile) || next.length > 1 ? next : next[0];
		}
		return changed;
	}

	private rewriteReusableProfileSelections(oldName: string, newName: string | undefined): void {
		if (this.runtimeResourceProfiles?.includes(oldName)) {
			this.runtimeResourceProfiles = [
				...new Set(
					this.runtimeResourceProfiles.flatMap((profile) =>
						profile === oldName ? (newName ? [newName] : []) : [profile],
					),
				),
			];
		}

		if (this.rewriteActiveProfileReference(this.globalSettings, oldName, newName)) {
			this.markModified("activeResourceProfile");
			this.markModified("activeResourceProfiles");
			this.save();
		}

		const projectSettings = structuredClone(this.projectSettings);
		if (this.rewriteActiveProfileReference(projectSettings, oldName, newName)) {
			this.markProjectModified("activeResourceProfile");
			this.markProjectModified("activeResourceProfiles");
			this.saveProjectSettings(projectSettings);
		}

		if (this.rewriteActiveProfileReference(this.directoryProfileSettings, oldName, newName)) {
			this.persistDirectoryProfiles(() => {});
		}
		this.recomputeSettings();
	}

	private persistDirectoryProfiles(update: (settings: Settings) => void): void {
		const next = structuredClone(this.directoryProfileSettings);
		update(next);
		this.directoryProfileSettings = next;
		this.recomputeSettings();

		this.enqueueWrite("directoryProfile", () => {
			this.storage.withLock("directoryProfile", (current) => {
				const currentSettings = current
					? SettingsManager.migrateSettings(JSON.parse(current) as Record<string, unknown>)
					: {};
				const merged: Settings = {
					...currentSettings,
					...next,
					resourceProfiles: next.resourceProfiles,
					activeResourceProfiles: next.activeResourceProfiles,
					activeResourceProfile: next.activeResourceProfile,
				};
				if (!next.resourceProfiles) {
					delete merged.resourceProfiles;
				}
				if (!next.activeResourceProfiles && next.activeResourceProfile === undefined) {
					delete merged.activeResourceProfiles;
					delete merged.activeResourceProfile;
				}
				return JSON.stringify(merged, null, 2);
			});
		});
	}

	private getProfileFilePath(profileName: string): string {
		const normalized = this.normalizeProfileName(profileName);
		const profilesDir = this.storage.getProfilesDir?.();
		if (!profilesDir) {
			throw new Error("Profiles directory is not configured");
		}
		return join(resolve(profilesDir), `${normalized}.json`);
	}

	/**
	 * Create or update a profile definition in the selected persistence scope.
	 */
	setProfileDefinition(profileName: string, definition: ProfileDefinitionInput, scope: ProfilePersistenceScope): void {
		const name = this.normalizeProfileName(profileName);
		const resources = this.sanitizeProfileResources(definition.resources);

		if (scope === "session") {
			const next = { ...this.inlineResourceProfileDefinitions };
			const payload = this.mergeProfileDefinition(name, definition, resources, next[name]);
			if (this.encodeStoredProfileDefinition(payload)) {
				next[name] = payload;
			} else {
				delete next[name];
			}
			this.inlineResourceProfileDefinitions = next;
			this.refreshProfileRegistry();
			return;
		}

		if (scope === "global") {
			const next = structuredClone(this.globalSettings);
			this.updateStoredProfileDefinition(next, name, definition, resources);
			this.globalSettings = next;
			this.markModified("resourceProfiles");
			this.save();
			return;
		}

		if (scope === "project") {
			this.updateProjectSettings("resourceProfiles", (settings) => {
				this.updateStoredProfileDefinition(settings, name, definition, resources);
			});
			return;
		}

		if (scope === "directory") {
			this.persistDirectoryProfiles((current) => {
				this.updateStoredProfileDefinition(current, name, definition, resources);
			});
			return;
		}

		const path = this.getProfileFilePath(name);
		const existing = existsSync(path)
			? parseProfileFileDefinition(readFileSync(path, "utf-8"), name)
			: { name, resources: {} };
		const payload = this.mergeProfileDefinition(name, definition, resources, existing);
		mkdirSync(dirname(path), { recursive: true });
		writeFileSync(path, JSON.stringify(payload, null, 2), "utf-8");
	}

	/**
	 * Delete a profile from the selected scope.
	 */
	deleteProfile(profileName: string, scope: ProfilePersistenceScope): void {
		const name = this.normalizeProfileName(profileName);

		if (scope === "session") {
			const next = { ...this.inlineResourceProfileDefinitions };
			delete next[name];
			this.inlineResourceProfileDefinitions = next;
			this.refreshProfileRegistry();
			if (this.runtimeResourceProfiles) {
				this.setRuntimeResourceProfiles(this.runtimeResourceProfiles.filter((profile) => profile !== name));
			}
			return;
		}

		if (scope === "global") {
			const next = { ...(this.globalSettings.resourceProfiles ?? {}) };
			delete next[name];
			if (Object.keys(next).length > 0) {
				this.globalSettings.resourceProfiles = next;
			} else {
				delete this.globalSettings.resourceProfiles;
			}
			if (this.globalSettings.activeResourceProfile === name) {
				delete this.globalSettings.activeResourceProfile;
			}
			if (this.globalSettings.activeResourceProfiles) {
				this.globalSettings.activeResourceProfiles = this.globalSettings.activeResourceProfiles.filter(
					(profile) => profile !== name,
				);
				if (this.globalSettings.activeResourceProfiles.length === 0) {
					delete this.globalSettings.activeResourceProfiles;
				}
			}
			this.markModified("resourceProfiles");
			this.save();
			return;
		}

		if (scope === "project") {
			this.updateProjectSettings("resourceProfiles", (settings) => {
				const next = { ...(settings.resourceProfiles ?? {}) };
				delete next[name];
				if (Object.keys(next).length > 0) {
					settings.resourceProfiles = next;
				} else {
					delete settings.resourceProfiles;
				}
				if (settings.activeResourceProfile === name) {
					delete settings.activeResourceProfile;
				}
				if (settings.activeResourceProfiles) {
					settings.activeResourceProfiles = settings.activeResourceProfiles.filter((profile) => profile !== name);
					if (settings.activeResourceProfiles.length === 0) {
						delete settings.activeResourceProfiles;
					}
				}
			});
			return;
		}

		if (scope === "directory") {
			this.persistDirectoryProfiles((current) => {
				const next = { ...(current.resourceProfiles ?? {}) };
				delete next[name];
				if (Object.keys(next).length > 0) {
					current.resourceProfiles = next;
				} else {
					delete current.resourceProfiles;
				}
				if (current.activeResourceProfile === name) {
					delete current.activeResourceProfile;
				}
				if (current.activeResourceProfiles) {
					current.activeResourceProfiles = current.activeResourceProfiles.filter((profile) => profile !== name);
					if (current.activeResourceProfiles.length === 0) {
						delete current.activeResourceProfiles;
					}
				}
			});
			return;
		}

		const profilePath = this.getProfileFilePath(name);
		if (!existsSync(profilePath)) {
			throw new Error(`Profile not found: ${name}`);
		}
		rmSync(profilePath, { force: true });
		this.rewriteReusableProfileSelections(name, undefined);
	}

	renameProfile(profileName: string, newProfileName: string, scope: ProfilePersistenceScope): void {
		const oldName = this.normalizeProfileName(profileName);
		const newName = this.normalizeProfileName(newProfileName);
		if (oldName === newName) {
			return;
		}

		if (scope === "session") {
			const profile = this.inlineResourceProfileDefinitions[oldName];
			if (!profile) {
				throw new Error(`Profile not found: ${oldName}`);
			}
			delete this.inlineResourceProfileDefinitions[oldName];
			this.inlineResourceProfileDefinitions[newName] = profile;
			if (this.runtimeResourceProfiles) {
				this.runtimeResourceProfiles = this.runtimeResourceProfiles.map((name) =>
					name === oldName ? newName : name,
				);
				this.setRuntimeResourceProfiles(this.runtimeResourceProfiles);
			}
			this.refreshProfileRegistry();
			return;
		}

		if (scope === "global") {
			const next = structuredClone(this.globalSettings);
			this.renameStoredProfileDefinition(next, oldName, newName);
			this.globalSettings = next;
			this.markModified("resourceProfiles");
			this.markModified("activeResourceProfile");
			this.markModified("activeResourceProfiles");
			this.save();
			return;
		}

		if (scope === "project") {
			this.updateProjectSettings("resourceProfiles", (settings) => {
				this.renameStoredProfileDefinition(settings, oldName, newName);
			});
			return;
		}

		if (scope === "directory") {
			this.persistDirectoryProfiles((current) => {
				this.renameStoredProfileDefinition(current, oldName, newName);
			});
			return;
		}

		const oldPath = this.getProfileFilePath(oldName);
		const newPath = this.getProfileFilePath(newName);
		if (!existsSync(oldPath)) {
			throw new Error(`Profile not found: ${oldName}`);
		}
		if (existsSync(newPath)) {
			throw new Error(`Profile already exists: ${newName}`);
		}
		const parsed = parseProfileFileDefinition(readFileSync(oldPath, "utf-8"), oldName);
		parsed.name = newName;
		writeFileSync(newPath, JSON.stringify(parsed, null, 2), "utf-8");
		rmSync(oldPath, { force: true });
		this.rewriteReusableProfileSelections(oldName, newName);
	}

	/**
	 * Set active profile selection in the selected scope.
	 */
	setActiveProfile(profileName: string | undefined, scope: Exclude<ProfilePersistenceScope, "reusable-file">): void {
		const name = profileName ? this.normalizeProfileSelection(profileName) : undefined;
		if (scope === "session") {
			if (name) {
				this.setRuntimeResourceProfiles([name]);
			} else {
				this.setRuntimeResourceProfiles([]);
			}
			return;
		}

		if (scope === "global") {
			if (name) {
				this.globalSettings.activeResourceProfile = name;
				this.globalSettings.activeResourceProfiles = [name];
			} else {
				delete this.globalSettings.activeResourceProfile;
				this.globalSettings.activeResourceProfiles = [];
			}
			this.markModified("activeResourceProfile");
			this.markModified("activeResourceProfiles");
			this.save();
			return;
		}

		if (scope === "project") {
			this.updateProjectSettings("activeResourceProfile", (settings) => {
				this.setActiveProfileInSettings(settings, name);
			});
			return;
		}

		this.persistDirectoryProfiles((current) => {
			this.setActiveProfileInSettings(current, name);
		});
	}

	/** Atomically replace the global profile/authority fields used by config restore and rollback. */
	replaceGlobalResourceProfileConfiguration(configuration: GlobalResourceProfileConfiguration): void {
		const next = structuredClone(this.globalSettings);
		const replace = <K extends keyof GlobalResourceProfileConfiguration>(key: K): void => {
			const value = configuration[key];
			if (value === undefined) {
				delete next[key];
			} else {
				next[key] = structuredClone(value) as Settings[K];
			}
			this.markModified(key);
		};
		replace("resourceProfiles");
		replace("activeResourceProfile");
		replace("activeResourceProfiles");
		replace("externalResourceRoots");
		replace("trustedResourceRoots");
		this.globalSettings = next;
		this.save();
	}

	/** Restore one profile definition's persistent owner after a post-doctor commit failure. */
	restoreProfileDefinitionFromReloadSnapshot(
		profileName: string,
		scope: Exclude<ProfilePersistenceScope, "reusable-file">,
		snapshot: SettingsReloadSnapshot,
	): void {
		const name = this.normalizeProfileName(profileName);
		if (scope === "session") {
			const previous = snapshot.inlineResourceProfileDefinitions[name];
			if (previous) this.inlineResourceProfileDefinitions[name] = structuredClone(previous);
			else delete this.inlineResourceProfileDefinitions[name];
			this.refreshProfileRegistry();
			return;
		}

		const restoreDefinition = (settings: Settings, previous: Settings): void => {
			const definitions = structuredClone(settings.resourceProfiles ?? {});
			const oldDefinition = previous.resourceProfiles?.[name];
			if (oldDefinition) definitions[name] = structuredClone(oldDefinition);
			else delete definitions[name];
			if (Object.keys(definitions).length > 0) settings.resourceProfiles = definitions;
			else delete settings.resourceProfiles;
		};

		if (scope === "global") {
			restoreDefinition(this.globalSettings, snapshot.globalSettings);
			this.markModified("resourceProfiles");
			this.save();
			return;
		}
		if (scope === "project") {
			this.updateProjectSettings("resourceProfiles", (settings) => {
				restoreDefinition(settings, snapshot.projectSettings);
			});
			return;
		}
		this.persistDirectoryProfiles((settings) => {
			restoreDefinition(settings, snapshot.directoryProfileSettings);
		});
	}

	/** Mark a global field as modified during this session */
	private markModified(field: keyof Settings, nestedKey?: string): void {
		this.modifiedFields.add(field);
		if (nestedKey) {
			if (!this.modifiedNestedFields.has(field)) {
				this.modifiedNestedFields.set(field, new Set());
			}
			this.modifiedNestedFields.get(field)!.add(nestedKey);
		}
	}

	/** Mark a project field as modified during this session */
	private markProjectModified(field: keyof Settings, nestedKey?: string): void {
		this.modifiedProjectFields.add(field);
		if (nestedKey) {
			if (!this.modifiedProjectNestedFields.has(field)) {
				this.modifiedProjectNestedFields.set(field, new Set());
			}
			this.modifiedProjectNestedFields.get(field)!.add(nestedKey);
		}
	}

	private assertProjectTrustedForWrite(): void {
		if (!this.projectTrusted) {
			throw new Error("Project is not trusted; refusing to write project settings");
		}
	}

	private recordError(scope: SettingsErrorScope, error: unknown): void {
		const normalizedError = error instanceof Error ? error : new Error(String(error));
		this.errors.push({ scope, error: normalizedError });
	}

	private clearModifiedScope(scope: SettingsScope): void {
		if (scope === "global") {
			this.modifiedFields.clear();
			this.modifiedNestedFields.clear();
			return;
		}

		this.modifiedProjectFields.clear();
		this.modifiedProjectNestedFields.clear();
	}

	private enqueueWrite(scope: SettingsScope, task: () => void): void {
		// Zero-footprint (worker session): this is the universal disk-write choke for every settings
		// scope (save() -> "global", saveProjectSettings() -> "project", directoryProfile writes) --
		// a worker session never writes settings.json (or any scoped variant) to disk. In-memory
		// settings are already updated by the caller before reaching here, so reads are unaffected.
		if (isWorkerSession()) return;
		this.writeQueue = this.writeQueue
			.then(() => {
				if (scope === "project") {
					this.assertProjectTrustedForWrite();
				}
				task();
				this.clearModifiedScope(scope);
			})
			.catch((error) => {
				this.recordError(scope, error);
			});
	}

	private cloneModifiedNestedFields(source: Map<keyof Settings, Set<string>>): Map<keyof Settings, Set<string>> {
		const snapshot = new Map<keyof Settings, Set<string>>();
		for (const [key, value] of source.entries()) {
			snapshot.set(key, new Set(value));
		}
		return snapshot;
	}

	private persistScopedSettings(
		scope: SettingsScope,
		snapshotSettings: Settings,
		modifiedFields: Set<keyof Settings>,
		modifiedNestedFields: Map<keyof Settings, Set<string>>,
	): void {
		this.storage.withLock(scope, (current) => {
			const currentFileSettings = current
				? SettingsManager.migrateSettings(JSON.parse(current) as Record<string, unknown>)
				: {};
			const mergedSettings: Settings = { ...currentFileSettings };
			for (const field of modifiedFields) {
				const value = snapshotSettings[field];
				if (modifiedNestedFields.has(field) && typeof value === "object" && value !== null) {
					const nestedModified = modifiedNestedFields.get(field)!;
					const baseNested = (currentFileSettings[field] as Record<string, unknown>) ?? {};
					const inMemoryNested = value as Record<string, unknown>;
					const mergedNested = { ...baseNested };
					for (const nestedKey of nestedModified) {
						mergedNested[nestedKey] = inMemoryNested[nestedKey];
					}
					(mergedSettings as Record<string, unknown>)[field] = mergedNested;
				} else {
					(mergedSettings as Record<string, unknown>)[field] = value;
				}
			}

			return JSON.stringify(mergedSettings, null, 2);
		});
	}

	private save(): void {
		this.recomputeSettings();

		if (this.globalSettingsLoadError) {
			return;
		}

		const snapshotGlobalSettings = structuredClone(this.globalSettings);
		const modifiedFields = new Set(this.modifiedFields);
		const modifiedNestedFields = this.cloneModifiedNestedFields(this.modifiedNestedFields);

		this.enqueueWrite("global", () => {
			this.persistScopedSettings("global", snapshotGlobalSettings, modifiedFields, modifiedNestedFields);
		});
	}

	private saveProjectSettings(settings: Settings): void {
		this.assertProjectTrustedForWrite();
		this.projectSettings = structuredClone(settings);
		this.recomputeSettings();

		if (this.projectSettingsLoadError) {
			return;
		}

		const snapshotProjectSettings = structuredClone(this.projectSettings);
		const modifiedFields = new Set(this.modifiedProjectFields);
		const modifiedNestedFields = this.cloneModifiedNestedFields(this.modifiedProjectNestedFields);
		this.enqueueWrite("project", () => {
			this.persistScopedSettings("project", snapshotProjectSettings, modifiedFields, modifiedNestedFields);
		});
	}

	private updateProjectSettings(field: keyof Settings, update: (settings: Settings) => void): void {
		this.assertProjectTrustedForWrite();
		const projectSettings = structuredClone(this.projectSettings);
		update(projectSettings);
		this.markProjectModified(field);
		this.saveProjectSettings(projectSettings);
	}

	async flush(): Promise<void> {
		await this.writeQueue;
	}

	drainErrors(): SettingsError[] {
		const drained = [...this.errors];
		this.errors = [];
		return drained;
	}

	getLastChangelogVersion(): string | undefined {
		return this.settings.lastChangelogVersion;
	}

	setLastChangelogVersion(version: string): void {
		this.globalSettings.lastChangelogVersion = version;
		this.markModified("lastChangelogVersion");
		this.save();
	}

	getSessionDir(): string | undefined {
		const sessionDir = this.settings.sessionDir;
		return sessionDir ? normalizePath(sessionDir) : sessionDir;
	}

	getDefaultProvider(): string | undefined {
		return this.settings.defaultProvider;
	}

	getDefaultModel(): string | undefined {
		return this.settings.defaultModel;
	}

	setDefaultProvider(provider: string): void {
		this.globalSettings.defaultProvider = provider;
		this.markModified("defaultProvider");
		this.save();
	}

	setDefaultModel(modelId: string): void {
		this.globalSettings.defaultModel = modelId;
		this.markModified("defaultModel");
		this.save();
	}

	setDefaultModelAndProvider(provider: string, modelId: string): void {
		this.globalSettings.defaultProvider = provider;
		this.globalSettings.defaultModel = modelId;
		this.markModified("defaultProvider");
		this.markModified("defaultModel");
		this.save();
	}

	getSteeringMode(): "all" | "one-at-a-time" {
		return this.settings.steeringMode || "one-at-a-time";
	}

	setSteeringMode(mode: "all" | "one-at-a-time"): void {
		this.globalSettings.steeringMode = mode;
		this.markModified("steeringMode");
		this.save();
	}

	getFollowUpMode(): "all" | "one-at-a-time" {
		return this.settings.followUpMode || "one-at-a-time";
	}

	setFollowUpMode(mode: "all" | "one-at-a-time"): void {
		this.globalSettings.followUpMode = mode;
		this.markModified("followUpMode");
		this.save();
	}

	getTheme(): string | undefined {
		return this.settings.theme;
	}

	setTheme(theme: string): void {
		this.globalSettings.theme = theme;
		this.markModified("theme");
		this.save();
	}

	/** The configured resource catalog directory, if any (round resource management). */
	getCatalogDir(): string | undefined {
		return this.settings.catalogDir;
	}

	setCatalogDir(dir: string | undefined): void {
		if (dir) {
			this.globalSettings.catalogDir = dir;
		} else {
			delete this.globalSettings.catalogDir;
		}
		this.markModified("catalogDir");
		this.save();
	}

	getDefaultThinkingLevel(): ThinkingLevel | undefined {
		return this.settings.defaultThinkingLevel;
	}

	setDefaultThinkingLevel(level: ThinkingLevel): void {
		this.globalSettings.defaultThinkingLevel = level;
		this.markModified("defaultThinkingLevel");
		this.save();
	}

	getFastModeEnabled(provider: string): boolean | undefined {
		const enabled = this.settings.fastMode?.[provider];
		return typeof enabled === "boolean" ? enabled : undefined;
	}

	setFastModeEnabled(provider: string, enabled: boolean): void {
		if (!provider || provider === "__proto__" || provider === "constructor" || provider === "prototype") {
			throw new TypeError(`Invalid fast-mode provider '${provider}'.`);
		}
		this.globalSettings.fastMode = { ...(this.globalSettings.fastMode ?? {}), [provider]: enabled };
		this.markModified("fastMode", provider);
		this.save();
	}

	getTransport(): TransportSetting {
		return this.settings.transport ?? "auto";
	}

	setTransport(transport: TransportSetting): void {
		this.globalSettings.transport = transport;
		this.markModified("transport");
		this.save();
	}

	getCompactionEnabled(): boolean {
		return this.settings.compaction?.enabled ?? DEFAULT_COMPACTION_SETTINGS.enabled;
	}

	setCompactionEnabled(enabled: boolean): void {
		if (!this.globalSettings.compaction) {
			this.globalSettings.compaction = {};
		}
		this.globalSettings.compaction.enabled = enabled;
		this.markModified("compaction", "enabled");
		this.save();
	}

	getCompactionReserveTokens(): number {
		return this.settings.compaction?.reserveTokens ?? DEFAULT_COMPACTION_SETTINGS.reserveTokens;
	}

	getCompactionKeepRecentTokens(): number {
		return this.settings.compaction?.keepRecentTokens ?? DEFAULT_COMPACTION_SETTINGS.keepRecentTokens;
	}

	getCompactionTriggerPercent(): number {
		const triggerPercent = this.settings.compaction?.triggerPercent ?? DEFAULT_COMPACTION_SETTINGS.triggerPercent;
		if (triggerPercent === undefined) throw new Error("default compaction triggerPercent is not configured");
		return triggerPercent;
	}

	hasExplicitCompactionTriggerPercent(): boolean {
		return this.settings.compaction?.triggerPercent !== undefined;
	}

	/**
	 * Skill curator (#32). Auto-archive of stale reflection-promoted skills is ON by default (restorable,
	 * announced, promoted-only). Set `autoArchive: false` to make it propose-only (`/curate`).
	 */
	getCuratorSettings(): { autoArchive: boolean; staleDays: number } {
		return {
			autoArchive: this.settings.curator?.autoArchive ?? true,
			staleDays: this.settings.curator?.staleDays ?? 30,
		};
	}

	/**
	 * Optional per-turn cost guard (#34). Explicit `enabled: true` is mandatory; this keeps positive
	 * thresholds written by older releases dormant instead of silently restoring a spend guard.
	 */
	getCostGuardSettings(): CostGuardSettings {
		const configuredMaxTurnUsd = this.settings.costGuard?.maxTurnUsd;
		return {
			enabled: this.settings.costGuard?.enabled === true,
			maxTurnUsd:
				typeof configuredMaxTurnUsd === "number" &&
				Number.isFinite(configuredMaxTurnUsd) &&
				configuredMaxTurnUsd >= 0
					? configuredMaxTurnUsd
					: DEFAULT_COST_GUARD_SETTINGS.maxTurnUsd,
			action: this.settings.costGuard?.action === "downgrade" ? "downgrade" : DEFAULT_COST_GUARD_SETTINGS.action,
		};
	}

	setCostGuardSettings(settings: CostGuardSettings, scope: SettingsScope = "global"): void {
		const normalized: CostGuardSettings = {
			enabled: settings.enabled === true,
			maxTurnUsd:
				Number.isFinite(settings.maxTurnUsd) && settings.maxTurnUsd >= 0
					? settings.maxTurnUsd
					: DEFAULT_COST_GUARD_SETTINGS.maxTurnUsd,
			action: settings.action === "downgrade" ? "downgrade" : "warn",
		};
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.costGuard = normalized;
			this.markProjectModified("costGuard");
			this.saveProjectSettings(projectSettings);
			return;
		}
		this.globalSettings.costGuard = normalized;
		this.markModified("costGuard");
		this.save();
	}

	getFailoverSettings(): Required<FailoverSettings> {
		return { subscriptionHop: this.settings.failover?.subscriptionHop ?? true };
	}

	private getProfileModelRouterSettings(): ModelRouterSettings | undefined {
		const activeProfileNames = this.getActiveResourceProfileNames();
		if (activeProfileNames.length === 0) return undefined;
		const registry = this.getProfileRegistry();
		const merged: ModelRouterSettings = {};
		for (let index = activeProfileNames.length - 1; index >= 0; index--) {
			const profile = this.resolveProfileFromRegistry(registry, activeProfileNames[index]);
			const router = profile?.modelRouter;
			if (!router) continue;
			if (router.enabled !== undefined) merged.enabled = router.enabled;
			if (router.judgeEnabled !== undefined) merged.judgeEnabled = router.judgeEnabled;
			if (router.fitnessGate !== undefined) merged.fitnessGate = router.fitnessGate;
			if (router.cheapModel !== undefined) merged.cheapModel = router.cheapModel;
			if (router.mediumModel !== undefined) merged.mediumModel = router.mediumModel;
			if (router.expensiveModel !== undefined) merged.expensiveModel = router.expensiveModel;
			if (router.learningModel !== undefined) merged.learningModel = router.learningModel;
			if (router.judgeModel !== undefined) merged.judgeModel = router.judgeModel;
			if (router.executorModel !== undefined) merged.executorModel = router.executorModel;
			if (router.cheapThinking !== undefined) merged.cheapThinking = router.cheapThinking;
			if (router.mediumThinking !== undefined) merged.mediumThinking = router.mediumThinking;
			if (router.expensiveThinking !== undefined) merged.expensiveThinking = router.expensiveThinking;
			if (router.executorThinking !== undefined) merged.executorThinking = router.executorThinking;
			if (router.judgeThinking !== undefined) merged.judgeThinking = router.judgeThinking;
		}
		return Object.keys(merged).length > 0 ? merged : undefined;
	}

	getModelRouterSettings(): {
		enabled: boolean;
		cheapModel?: string;
		mediumModel?: string;
		expensiveModel?: string;
		learningModel?: string;
		judgeEnabled: boolean;
		judgeModel?: string;
		executorModel?: string;
		fitnessGate: boolean;
		cheapThinking?: ThinkingLevel;
		mediumThinking?: ThinkingLevel;
		expensiveThinking?: ThinkingLevel;
		executorThinking?: ThinkingLevel;
		judgeThinking?: ThinkingLevel;
	} {
		const profileSettings = this.getProfileModelRouterSettings();
		const settings = {
			enabled: this.settings.modelRouter?.enabled ?? false,
			cheapModel: this.settings.modelRouter?.cheapModel?.trim() || undefined,
			mediumModel: this.settings.modelRouter?.mediumModel?.trim() || undefined,
			expensiveModel: this.settings.modelRouter?.expensiveModel?.trim() || undefined,
			learningModel: this.settings.modelRouter?.learningModel?.trim() || undefined,
			judgeEnabled: this.settings.modelRouter?.judgeEnabled ?? true,
			fitnessGate: this.settings.modelRouter?.fitnessGate ?? false,
			judgeModel: this.settings.modelRouter?.judgeModel?.trim() || undefined,
			executorModel: this.settings.modelRouter?.executorModel?.trim() || undefined,
			cheapThinking: isThinkingLevel(this.settings.modelRouter?.cheapThinking)
				? this.settings.modelRouter?.cheapThinking
				: undefined,
			mediumThinking: isThinkingLevel(this.settings.modelRouter?.mediumThinking)
				? this.settings.modelRouter?.mediumThinking
				: undefined,
			expensiveThinking: isThinkingLevel(this.settings.modelRouter?.expensiveThinking)
				? this.settings.modelRouter?.expensiveThinking
				: undefined,
			executorThinking: isThinkingLevel(this.settings.modelRouter?.executorThinking)
				? this.settings.modelRouter?.executorThinking
				: undefined,
			judgeThinking: isThinkingLevel(this.settings.modelRouter?.judgeThinking)
				? this.settings.modelRouter?.judgeThinking
				: undefined,
		};
		return {
			enabled: profileSettings?.enabled ?? settings.enabled,
			cheapModel: profileSettings?.cheapModel?.trim() || settings.cheapModel,
			mediumModel: profileSettings?.mediumModel?.trim() || settings.mediumModel,
			expensiveModel: profileSettings?.expensiveModel?.trim() || settings.expensiveModel,
			learningModel: profileSettings?.learningModel?.trim() || settings.learningModel,
			judgeEnabled: profileSettings?.judgeEnabled ?? settings.judgeEnabled,
			fitnessGate: profileSettings?.fitnessGate ?? settings.fitnessGate,
			judgeModel: profileSettings?.judgeModel?.trim() || settings.judgeModel,
			executorModel: profileSettings?.executorModel?.trim() || settings.executorModel,
			cheapThinking: profileSettings?.cheapThinking ?? settings.cheapThinking,
			mediumThinking: profileSettings?.mediumThinking ?? settings.mediumThinking,
			expensiveThinking: profileSettings?.expensiveThinking ?? settings.expensiveThinking,
			executorThinking: profileSettings?.executorThinking ?? settings.executorThinking,
			judgeThinking: profileSettings?.judgeThinking ?? settings.judgeThinking,
		};
	}

	setModelRouterSettings(settings: ModelRouterSettings, scope: SettingsScope = "global"): void {
		const normalized: ModelRouterSettings = {
			enabled: settings.enabled ?? false,
			cheapModel: settings.cheapModel?.trim() || undefined,
			mediumModel: settings.mediumModel?.trim() || undefined,
			expensiveModel: settings.expensiveModel?.trim() || undefined,
			learningModel: settings.learningModel?.trim() || undefined,
			judgeEnabled: settings.judgeEnabled ?? true,
			fitnessGate: settings.fitnessGate ?? false,
			judgeModel: settings.judgeModel?.trim() || undefined,
			executorModel: settings.executorModel?.trim() || undefined,
			cheapThinking: isThinkingLevel(settings.cheapThinking) ? settings.cheapThinking : undefined,
			mediumThinking: isThinkingLevel(settings.mediumThinking) ? settings.mediumThinking : undefined,
			expensiveThinking: isThinkingLevel(settings.expensiveThinking) ? settings.expensiveThinking : undefined,
			executorThinking: isThinkingLevel(settings.executorThinking) ? settings.executorThinking : undefined,
			judgeThinking: isThinkingLevel(settings.judgeThinking) ? settings.judgeThinking : undefined,
		};
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.modelRouter = normalized;
			this.markProjectModified("modelRouter");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.modelRouter = normalized;
		this.markModified("modelRouter");
		this.save();
	}

	/** Configured auxiliary summarizer model id, or "auto" (default) to pick the cheapest authed model. */
	getCompactionModel(): string {
		return this.settings.compaction?.model ?? "auto";
	}

	getCompactionSettings(): {
		enabled: boolean;
		reserveTokens: number;
		keepRecentTokens: number;
		triggerPercent: number;
	} {
		return {
			enabled: this.getCompactionEnabled(),
			reserveTokens: this.getCompactionReserveTokens(),
			keepRecentTokens: this.getCompactionKeepRecentTokens(),
			triggerPercent: this.getCompactionTriggerPercent(),
		};
	}

	getScoutSettings(): { enabled: boolean; model: string } {
		return {
			enabled: this.settings.scout?.enabled ?? false,
			model: this.settings.scout?.model ?? "auto",
		};
	}

	setScoutSettings(settings: ScoutSettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.scout = { ...settings };
			this.markProjectModified("scout");
			this.saveProjectSettings(projectSettings);
			return;
		}
		this.globalSettings.scout = { ...settings };
		this.markModified("scout");
		this.save();
	}

	getContextGcSettings(): {
		enabled: boolean;
		preserveRecentMessages: number;
		minToolResultChars: number;
		tools: string[];
		semanticMemory: {
			enabled: boolean;
			preserveRecentPages: number;
			minChars: number;
			markers: string[];
		};
	} {
		// Per-field fallback throughout (never a whole-object `??`) so an explicit partial override --
		// e.g. only `contextGc.minToolResultChars`, or only `semanticMemory.enabled` -- still gets the
		// canonical default for every field it didn't set. Every fallback DERIVES from context-gc.ts's
		// exported DEFAULT_CONTEXT_GC_SETTINGS (the same object applyContextGc's own normalizer falls
		// back to) instead of a hand-kept copy, so this method and context-gc.ts's defaults can never
		// drift again -- hand-copied fallbacks here previously (a) missed the "<task_steps_context"
		// marker context-gc.ts's own default carries, silently disabling GC packing
		// for that page under default settings, and (b) diverged on the `tools` list (missing
		// "run_toolkit_script", present only here) -- both classes of drift are now structurally
		// impossible since there is exactly one place these defaults are written down.
		return {
			enabled: this.settings.contextGc?.enabled ?? DEFAULT_CONTEXT_GC_SETTINGS.enabled,
			preserveRecentMessages:
				this.settings.contextGc?.preserveRecentMessages ?? DEFAULT_CONTEXT_GC_SETTINGS.preserveRecentMessages,
			minToolResultChars:
				this.settings.contextGc?.minToolResultChars ?? DEFAULT_CONTEXT_GC_SETTINGS.minToolResultChars,
			tools: this.settings.contextGc?.tools ?? DEFAULT_CONTEXT_GC_SETTINGS.tools,
			semanticMemory: {
				enabled:
					this.settings.contextGc?.semanticMemory?.enabled ?? DEFAULT_CONTEXT_GC_SETTINGS.semanticMemory.enabled,
				preserveRecentPages:
					this.settings.contextGc?.semanticMemory?.preserveRecentPages ??
					DEFAULT_CONTEXT_GC_SETTINGS.semanticMemory.preserveRecentPages,
				minChars:
					this.settings.contextGc?.semanticMemory?.minChars ?? DEFAULT_CONTEXT_GC_SETTINGS.semanticMemory.minChars,
				markers:
					this.settings.contextGc?.semanticMemory?.markers ?? DEFAULT_CONTEXT_GC_SETTINGS.semanticMemory.markers,
			},
		};
	}

	getContextPromptEnforcementSettings(): { enabled: boolean; preserveRecentMessages: number; minChars: number } {
		// Enforcement's own default MIRRORS the live context-gc settings (not a second hardcoded
		// literal) so tuning contextGc.preserveRecentMessages/minToolResultChars moves this default
		// with it instead of silently drifting. An explicitly configured enforcement value always
		// wins over the mirror -- see the ContextPromptEnforcementSettings doc comment.
		const gcSettings = this.getContextGcSettings();
		return {
			enabled: this.settings.contextPolicy?.enforcement?.enabled ?? false,
			preserveRecentMessages:
				this.settings.contextPolicy?.enforcement?.preserveRecentMessages ?? gcSettings.preserveRecentMessages,
			minChars: this.settings.contextPolicy?.enforcement?.minChars ?? gcSettings.minToolResultChars,
		};
	}

	getContextCurationSettings(): { enabled: boolean; model?: string; maxJobsPerTurn: number } {
		return {
			enabled: this.settings.contextPolicy?.curation?.enabled ?? false,
			model: this.settings.contextPolicy?.curation?.model?.trim() || undefined,
			maxJobsPerTurn: sanitizeIntegerSetting(this.settings.contextPolicy?.curation?.maxJobsPerTurn, 4, 1, 16),
		};
	}

	setContextCurationSettings(settings: ContextCurationSettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.contextPolicy = { ...projectSettings.contextPolicy, curation: { ...settings } };
			this.markProjectModified("contextPolicy");
			this.saveProjectSettings(projectSettings);
			return;
		}
		this.globalSettings.contextPolicy = { ...this.globalSettings.contextPolicy, curation: { ...settings } };
		this.markModified("contextPolicy");
		this.save();
	}

	setContextPromptEnforcementSettings(
		settings: ContextPromptEnforcementSettings,
		scope: SettingsScope = "global",
	): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.contextPolicy = { ...projectSettings.contextPolicy, enforcement: { ...settings } };
			this.markProjectModified("contextPolicy");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.contextPolicy = { ...this.globalSettings.contextPolicy, enforcement: { ...settings } };
		this.markModified("contextPolicy");
		this.save();
	}

	getMemoryRetrievalSettings(): {
		enabled: boolean;
		maxResults: number;
		includeInPrompt: boolean;
		allowExternalEgress: boolean;
	} {
		return {
			enabled: this.settings.contextPolicy?.memory?.enabled ?? true,
			maxResults: clampMemoryRetrievalMaxResults(
				this.settings.contextPolicy?.memory?.maxResults ?? MEMORY_RETRIEVAL_MAX_RESULTS_DEFAULT,
			),
			includeInPrompt: this.settings.contextPolicy?.memory?.includeInPrompt ?? true,
			allowExternalEgress: this.settings.contextPolicy?.memory?.allowExternalEgress === true,
		};
	}

	setMemoryRetrievalSettings(settings: MemoryRetrievalSettings, scope: SettingsScope = "global"): void {
		const normalized: MemoryRetrievalSettings = {
			enabled: settings.enabled,
			maxResults:
				settings.maxResults === undefined ? undefined : clampMemoryRetrievalMaxResults(settings.maxResults),
			includeInPrompt: settings.includeInPrompt,
			allowExternalEgress: settings.allowExternalEgress,
		};
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.contextPolicy = { ...projectSettings.contextPolicy, memory: normalized };
			this.markProjectModified("contextPolicy");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.contextPolicy = { ...this.globalSettings.contextPolicy, memory: normalized };
		this.markModified("contextPolicy");
		this.save();
	}

	getBranchSummarySettings(): { reserveTokens: number; skipPrompt: boolean } {
		return {
			reserveTokens: this.settings.branchSummary?.reserveTokens ?? 16384,
			skipPrompt: this.settings.branchSummary?.skipPrompt ?? false,
		};
	}

	getBranchSummarySkipPrompt(): boolean {
		return this.settings.branchSummary?.skipPrompt ?? false;
	}

	getRetryEnabled(): boolean {
		return this.settings.retry?.enabled ?? true;
	}

	setRetryEnabled(enabled: boolean): void {
		if (!this.globalSettings.retry) {
			this.globalSettings.retry = {};
		}
		this.globalSettings.retry.enabled = enabled;
		this.markModified("retry", "enabled");
		this.save();
	}

	getRetrySettings(): { enabled: boolean; maxRetries: number; baseDelayMs: number } {
		return {
			enabled: this.getRetryEnabled(),
			maxRetries: this.settings.retry?.maxRetries ?? 3,
			baseDelayMs: this.settings.retry?.baseDelayMs ?? 2000,
		};
	}

	/**
	 * Stream-stall watchdog bounds (pi-agent-core reliability/watchdogs.ts). Returns only the
	 * fields the user set, validated; unset fields fall back to DEFAULT_STREAM_IDLE at the
	 * wiring site (agent-session constructor). Resolved per request, so edits apply live.
	 */
	getStreamStallSettings(): { connectMs?: number; activeIdleMs?: number; quietIdleMs?: number } {
		const stall = this.settings.retry?.stall;
		return {
			connectMs: parseStallBoundMs(stall?.connectMs, "retry.stall.connectMs"),
			activeIdleMs: parseStallBoundMs(stall?.activeIdleMs, "retry.stall.activeIdleMs"),
			quietIdleMs: parseStallBoundMs(stall?.quietIdleMs, "retry.stall.quietIdleMs"),
		};
	}

	getHttpIdleTimeoutMs(): number {
		return parseTimeoutSetting(this.settings.httpIdleTimeoutMs, "httpIdleTimeoutMs") ?? DEFAULT_HTTP_IDLE_TIMEOUT_MS;
	}

	setHttpIdleTimeoutMs(timeoutMs: number): void {
		if (!Number.isFinite(timeoutMs) || timeoutMs < 0) {
			throw new Error(`Invalid httpIdleTimeoutMs setting: ${String(timeoutMs)}`);
		}
		this.globalSettings.httpIdleTimeoutMs = Math.floor(timeoutMs);
		this.markModified("httpIdleTimeoutMs");
		this.save();
	}

	getProviderRetrySettings(): { timeoutMs?: number; maxRetries?: number; maxRetryDelayMs: number } {
		return {
			timeoutMs: this.settings.retry?.provider?.timeoutMs,
			maxRetries: this.settings.retry?.provider?.maxRetries,
			maxRetryDelayMs: this.settings.retry?.provider?.maxRetryDelayMs ?? 60000,
		};
	}

	getWebSocketConnectTimeoutMs(): number | undefined {
		return parseTimeoutSetting(this.settings.websocketConnectTimeoutMs, "websocketConnectTimeoutMs");
	}

	getHideThinkingBlock(): boolean {
		return this.settings.hideThinkingBlock ?? true;
	}

	setHideThinkingBlock(hide: boolean): void {
		this.globalSettings.hideThinkingBlock = hide;
		this.markModified("hideThinkingBlock");
		this.save();
	}

	getShellPath(): string | undefined {
		return this.settings.shellPath;
	}

	setShellPath(path: string | undefined): void {
		this.globalSettings.shellPath = path;
		this.markModified("shellPath");
		this.save();
	}

	getQuietStartup(): boolean {
		return this.settings.quietStartup ?? false;
	}

	/** Repository context files are off unless a settings layer opts in. Global agent-dir files always load. */
	getProjectContextFiles(): "on-demand" | "off" {
		return this.settings.projectContextFiles === "on-demand" ? "on-demand" : "off";
	}

	/** Which settings layer last set `projectContextFiles`, if any. */
	getProjectContextFilesScope(): SettingsScope | undefined {
		if (this.directoryProfileSettings.projectContextFiles !== undefined) return "directoryProfile";
		if (this.projectSettings.projectContextFiles !== undefined) return "project";
		if (this.globalSettings.projectContextFiles !== undefined) return "global";
		return undefined;
	}

	setProjectContextFiles(mode: "on-demand" | "off", scope: SettingsScope = "directoryProfile"): void {
		if (scope === "directoryProfile") {
			this.persistDirectoryProfiles((settings) => {
				settings.projectContextFiles = mode;
			});
			return;
		}
		if (scope === "project") {
			this.updateProjectSettings("projectContextFiles", (settings) => {
				settings.projectContextFiles = mode;
			});
			return;
		}
		this.globalSettings.projectContextFiles = mode;
		this.markModified("projectContextFiles");
		this.save();
	}

	setQuietStartup(quiet: boolean): void {
		this.globalSettings.quietStartup = quiet;
		this.markModified("quietStartup");
		this.save();
	}

	getShellCommandPrefix(): string | undefined {
		return this.settings.shellCommandPrefix;
	}

	setShellCommandPrefix(prefix: string | undefined): void {
		this.globalSettings.shellCommandPrefix = prefix;
		this.markModified("shellCommandPrefix");
		this.save();
	}

	getNpmCommand(): string[] | undefined {
		return this.settings.npmCommand ? [...this.settings.npmCommand] : undefined;
	}

	setNpmCommand(command: string[] | undefined): void {
		this.globalSettings.npmCommand = command ? [...command] : undefined;
		this.markModified("npmCommand");
		this.save();
	}

	getCollapseChangelog(): boolean {
		return this.settings.collapseChangelog ?? false;
	}

	setCollapseChangelog(collapse: boolean): void {
		this.globalSettings.collapseChangelog = collapse;
		this.markModified("collapseChangelog");
		this.save();
	}

	getEnableInstallTelemetry(): boolean {
		return this.settings.enableInstallTelemetry ?? true;
	}

	setEnableInstallTelemetry(enabled: boolean): void {
		this.globalSettings.enableInstallTelemetry = enabled;
		this.markModified("enableInstallTelemetry");
		this.save();
	}

	getPackages(): PackageSource[] {
		return [...(this.settings.packages ?? [])];
	}

	setPackages(packages: PackageSource[]): void {
		this.globalSettings.packages = packages;
		this.markModified("packages");
		this.save();
	}

	setProjectPackages(packages: PackageSource[]): void {
		this.updateProjectSettings("packages", (settings) => {
			settings.packages = packages;
		});
	}

	getExtensionPaths(): string[] {
		return [...(this.settings.extensions ?? [])];
	}

	setExtensionPaths(paths: string[]): void {
		this.globalSettings.extensions = paths;
		this.markModified("extensions");
		this.save();
	}

	setProjectExtensionPaths(paths: string[]): void {
		this.updateProjectSettings("extensions", (settings) => {
			settings.extensions = paths;
		});
	}

	getSkillPaths(): string[] {
		return [...(this.settings.skills ?? [])];
	}

	setSkillPaths(paths: string[]): void {
		this.globalSettings.skills = paths;
		this.markModified("skills");
		this.save();
	}

	setProjectSkillPaths(paths: string[]): void {
		this.updateProjectSettings("skills", (settings) => {
			settings.skills = paths;
		});
	}

	getPromptTemplatePaths(): string[] {
		return [...(this.settings.prompts ?? [])];
	}

	setPromptTemplatePaths(paths: string[]): void {
		this.globalSettings.prompts = paths;
		this.markModified("prompts");
		this.save();
	}

	setProjectPromptTemplatePaths(paths: string[]): void {
		this.updateProjectSettings("prompts", (settings) => {
			settings.prompts = paths;
		});
	}

	getThemePaths(): string[] {
		return [...(this.settings.themes ?? [])];
	}

	setThemePaths(paths: string[]): void {
		this.globalSettings.themes = paths;
		this.markModified("themes");
		this.save();
	}

	setProjectThemePaths(paths: string[]): void {
		this.updateProjectSettings("themes", (settings) => {
			settings.themes = paths;
		});
	}

	getEnableSkillCommands(): boolean {
		return this.settings.enableSkillCommands ?? true;
	}

	setEnableSkillCommands(enabled: boolean): void {
		this.globalSettings.enableSkillCommands = enabled;
		this.markModified("enableSkillCommands");
		this.save();
	}

	getThinkingBudgets(): ThinkingBudgetsSettings | undefined {
		return this.settings.thinkingBudgets;
	}

	getShowImages(): boolean {
		return this.settings.terminal?.showImages ?? true;
	}

	setShowImages(show: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.showImages = show;
		this.markModified("terminal", "showImages");
		this.save();
	}

	getImageWidthCells(): number {
		const width = this.settings.terminal?.imageWidthCells;
		if (typeof width !== "number" || !Number.isFinite(width)) {
			return 60;
		}
		return Math.max(1, Math.floor(width));
	}

	setImageWidthCells(width: number): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.imageWidthCells = Math.max(1, Math.floor(width));
		this.markModified("terminal", "imageWidthCells");
		this.save();
	}

	getClearOnShrink(): boolean {
		// Settings takes precedence, then env var, then default false
		if (this.settings.terminal?.clearOnShrink !== undefined) {
			return this.settings.terminal.clearOnShrink;
		}
		return process.env.PI_CLEAR_ON_SHRINK === "1";
	}

	setClearOnShrink(enabled: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.clearOnShrink = enabled;
		this.markModified("terminal", "clearOnShrink");
		this.save();
	}

	getShowTerminalProgress(): boolean {
		return this.settings.terminal?.showTerminalProgress ?? false;
	}

	setShowTerminalProgress(enabled: boolean): void {
		if (!this.globalSettings.terminal) {
			this.globalSettings.terminal = {};
		}
		this.globalSettings.terminal.showTerminalProgress = enabled;
		this.markModified("terminal", "showTerminalProgress");
		this.save();
	}

	getImageAutoResize(): boolean {
		return this.settings.images?.autoResize ?? true;
	}

	setImageAutoResize(enabled: boolean): void {
		if (!this.globalSettings.images) {
			this.globalSettings.images = {};
		}
		this.globalSettings.images.autoResize = enabled;
		this.markModified("images", "autoResize");
		this.save();
	}

	getBlockImages(): boolean {
		return this.settings.images?.blockImages ?? false;
	}

	setBlockImages(blocked: boolean): void {
		if (!this.globalSettings.images) {
			this.globalSettings.images = {};
		}
		this.globalSettings.images.blockImages = blocked;
		this.markModified("images", "blockImages");
		this.save();
	}

	getClipboardImageDirectory(): string | undefined {
		return this.settings.images?.clipboardDirectory?.trim() || undefined;
	}

	setClipboardImageDirectory(directory: string | undefined): void {
		if (!this.globalSettings.images) {
			this.globalSettings.images = {};
		}
		if (directory?.trim()) {
			this.globalSettings.images.clipboardDirectory = directory.trim();
		} else {
			delete this.globalSettings.images.clipboardDirectory;
		}
		this.markModified("images", "clipboardDirectory");
		this.save();
	}

	getEnabledModels(): string[] | undefined {
		return this.settings.enabledModels;
	}

	setEnabledModels(patterns: string[] | undefined): void {
		this.globalSettings.enabledModels = patterns;
		this.markModified("enabledModels");
		this.save();
	}

	getDoubleEscapeAction(): "fork" | "tree" | "none" {
		return this.settings.doubleEscapeAction ?? "tree";
	}

	setDoubleEscapeAction(action: "fork" | "tree" | "none"): void {
		this.globalSettings.doubleEscapeAction = action;
		this.markModified("doubleEscapeAction");
		this.save();
	}

	getTreeFilterMode(): "default" | "no-tools" | "user-only" | "labeled-only" | "all" {
		const mode = this.settings.treeFilterMode;
		const valid = ["default", "no-tools", "user-only", "labeled-only", "all"];
		return mode && valid.includes(mode) ? mode : "default";
	}

	setTreeFilterMode(mode: "default" | "no-tools" | "user-only" | "labeled-only" | "all"): void {
		this.globalSettings.treeFilterMode = mode;
		this.markModified("treeFilterMode");
		this.save();
	}

	getShowHardwareCursor(): boolean {
		return this.settings.showHardwareCursor ?? process.env.PI_HARDWARE_CURSOR === "1";
	}

	setShowHardwareCursor(enabled: boolean): void {
		this.globalSettings.showHardwareCursor = enabled;
		this.markModified("showHardwareCursor");
		this.save();
	}

	getEditorPaddingX(): number {
		return this.settings.editorPaddingX ?? 0;
	}

	setEditorPaddingX(padding: number): void {
		this.globalSettings.editorPaddingX = Math.max(0, Math.min(3, Math.floor(padding)));
		this.markModified("editorPaddingX");
		this.save();
	}

	getAutocompleteMaxVisible(): number {
		return this.settings.autocompleteMaxVisible ?? 5;
	}

	setAutocompleteMaxVisible(maxVisible: number): void {
		this.globalSettings.autocompleteMaxVisible = Math.max(3, Math.min(20, Math.floor(maxVisible)));
		this.markModified("autocompleteMaxVisible");
		this.save();
	}

	getCodeBlockIndent(): string {
		return this.settings.markdown?.codeBlockIndent ?? "  ";
	}

	getWarnings(): WarningSettings {
		return { ...(this.settings.warnings ?? {}) };
	}

	setWarnings(warnings: WarningSettings): void {
		this.globalSettings.warnings = { ...warnings };
		this.markModified("warnings");
		this.save();
	}

	getSelfModificationSettings(): { enabled: boolean; sourcePath?: string; sourcePaths?: string[] } {
		return {
			enabled: this.settings.selfModification?.enabled ?? false,
			sourcePath: this.settings.selfModification?.sourcePath,
			sourcePaths: this.settings.selfModification?.sourcePaths,
		};
	}

	setSelfModificationSettings(settings: SelfModificationSettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			const existing = projectSettings.selfModification;
			projectSettings.selfModification = {
				...existing,
				...settings,
				sourcePaths: settings.sourcePaths ?? existing?.sourcePaths,
			};
			this.markProjectModified("selfModification");
			this.saveProjectSettings(projectSettings);
			return;
		}

		const existing = this.globalSettings.selfModification;
		this.globalSettings.selfModification = {
			...existing,
			...settings,
			sourcePaths: settings.sourcePaths ?? existing?.sourcePaths,
		};
		this.markModified("selfModification");
		this.save();
	}

	getAutonomySettings(): Required<AutonomySettings> {
		const mode = this.settings.autonomy?.mode;
		const configuredMaxStallTurns = this.settings.autonomy?.maxStallTurns;
		const configuredGoalContinueTurns = this.settings.autonomy?.goalContinueTurns;
		const configuredGoalContinueMaxWallClockMinutes = this.settings.autonomy?.goalContinueMaxWallClockMinutes;
		const configuredGoalAutoContinue = this.settings.autonomy?.goalAutoContinue;
		const configuredGoalAutoContinueDelayMs = this.settings.autonomy?.goalAutoContinueDelayMs;

		const maxStallTurns = sanitizeIntegerSetting(
			configuredMaxStallTurns,
			DEFAULT_AUTONOMY_MAX_STALL_TURNS,
			0,
			MAX_GOAL_CONTINUE_MAX_STALL_TURNS,
		);
		const goalContinueTurns = sanitizeIntegerSetting(
			configuredGoalContinueTurns,
			DEFAULT_AUTONOMY_GOAL_CONTINUE_TURNS,
			0,
			MAX_GOAL_CONTINUE_MAX_TURNS,
		);
		const goalContinueMaxWallClockMinutes = sanitizeIntegerSetting(
			configuredGoalContinueMaxWallClockMinutes,
			DEFAULT_AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES,
			0,
			MAX_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES,
		);
		const goalAutoContinueDelayMs = sanitizeIntegerSetting(
			configuredGoalAutoContinueDelayMs,
			DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS,
			0,
			MAX_GOAL_AUTO_CONTINUE_DELAY_MS,
		);

		return {
			mode: mode === "safe" || mode === "balanced" || mode === "full" ? mode : "off",
			maxStallTurns,
			goalContinueTurns,
			goalContinueMaxWallClockMinutes,
			goalAutoContinue:
				typeof configuredGoalAutoContinue === "boolean"
					? configuredGoalAutoContinue
					: DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE,
			goalAutoContinueDelayMs,
		};
	}

	setAutonomySettings(settings: AutonomySettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.autonomy = { ...settings };
			this.markProjectModified("autonomy");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.autonomy = { ...settings };
		this.markModified("autonomy");
		this.save();
	}

	getResearchLaneSettings(): ResolvedResearchLaneSettings {
		const configured = this.settings.researchLane ?? {};

		const resolved: ResolvedResearchLaneSettings = {
			enabled: typeof configured.enabled === "boolean" ? configured.enabled : DEFAULT_RESEARCH_LANE_ENABLED,
			maxUsd: sanitizeNumberSetting(configured.maxUsd, DEFAULT_RESEARCH_LANE_MAX_USD, 0, MAX_RESEARCH_LANE_MAX_USD),
			maxSources: sanitizeIntegerSetting(
				configured.maxSources,
				DEFAULT_RESEARCH_LANE_MAX_SOURCES,
				1,
				MAX_RESEARCH_LANE_MAX_SOURCES,
			),
			maxFindings: sanitizeIntegerSetting(
				configured.maxFindings,
				DEFAULT_RESEARCH_LANE_MAX_FINDINGS,
				1,
				MAX_RESEARCH_LANE_MAX_FINDINGS,
			),
			maxWallClockMs: sanitizeIntegerSetting(
				configured.maxWallClockMs,
				DEFAULT_RESEARCH_LANE_MAX_WALL_CLOCK_MS,
				0,
				MAX_RESEARCH_LANE_MAX_WALL_CLOCK_MS,
			),
			idleDelayMs: sanitizeIntegerSetting(
				configured.idleDelayMs,
				DEFAULT_RESEARCH_LANE_IDLE_DELAY_MS,
				0,
				MAX_RESEARCH_LANE_IDLE_DELAY_MS,
			),
			maxRunsPerSession: sanitizeIntegerSetting(
				configured.maxRunsPerSession,
				DEFAULT_RESEARCH_LANE_MAX_RUNS_PER_SESSION,
				0,
				MAX_RESEARCH_LANE_MAX_RUNS_PER_SESSION,
			),
		};
		if (typeof configured.model === "string" && configured.model.trim().length > 0) {
			resolved.model = configured.model;
		}
		if (typeof configured.profile === "string" && configured.profile.trim().length > 0) {
			resolved.profile = configured.profile;
		}
		if (typeof configured.systemPrompt === "string" && configured.systemPrompt.trim().length > 0) {
			resolved.systemPrompt = configured.systemPrompt;
		}
		return resolved;
	}

	setResearchLaneSettings(settings: ResearchLaneSettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.researchLane = { ...settings };
			this.markProjectModified("researchLane");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.researchLane = { ...settings };
		this.markModified("researchLane");
		this.save();
	}

	getWorktreeSyncSettings(): ResolvedWorktreeSyncSettings {
		const configured = this.settings.worktreeSync ?? {};
		const resolved: ResolvedWorktreeSyncSettings = {
			enabled: configured.enabled !== false,
			syncPolicy:
				configured.syncPolicy === "overlap_mandatory" || configured.syncPolicy === "land_time_only"
					? configured.syncPolicy
					: DEFAULT_WORKTREE_SYNC_POLICY,
			gate: configured.gate === "off" ? "off" : "on",
			gateTimeoutMs: sanitizeIntegerSetting(
				configured.gateTimeoutMs,
				DEFAULT_WORKTREE_SYNC_GATE_TIMEOUT_MS,
				1000,
				3_600_000,
			),
			maxLanes: sanitizeIntegerSetting(configured.maxLanes, DEFAULT_WORKTREE_SYNC_MAX_LANES, 1, 32),
			workerLand: configured.workerLand === "allow" ? "allow" : "deny",
		};
		if (typeof configured.mainBranch === "string" && configured.mainBranch.trim().length > 0) {
			resolved.mainBranch = configured.mainBranch.trim();
		}
		if (typeof configured.gateCommand === "string" && configured.gateCommand.trim().length > 0) {
			resolved.gateCommand = configured.gateCommand.trim();
		}
		if (typeof configured.worktreesRoot === "string" && configured.worktreesRoot.trim().length > 0) {
			resolved.worktreesRoot = configured.worktreesRoot.trim();
		}
		return resolved;
	}

	getProcessMatrixSettings(): ResolvedProcessMatrixSettings {
		const configured = this.settings.processMatrix ?? {};
		return {
			enabled: configured.enabled !== false,
			heartbeatMs: sanitizeIntegerSetting(
				configured.heartbeatMs,
				DEFAULT_PROCESS_MATRIX_HEARTBEAT_MS,
				1000,
				600_000,
			),
			adoptionGraceMs: sanitizeIntegerSetting(
				configured.adoptionGraceMs,
				DEFAULT_PROCESS_MATRIX_ADOPTION_GRACE_MS,
				5000,
				3_600_000,
			),
			watcherPollMs: sanitizeIntegerSetting(
				configured.watcherPollMs,
				DEFAULT_PROCESS_MATRIX_WATCHER_POLL_MS,
				1000,
				600_000,
			),
		};
	}

	getWindowsShellSettings(): ResolvedWindowsShellSettings {
		const configured = this.settings.windowsShell ?? {};
		return {
			pythonEngine: configured.pythonEngine !== false,
		};
	}

	getWorkerDelegationSettings(): ResolvedWorkerDelegationSettings {
		const configured = normalizeWorkerDelegationLayer(this.settings.workerDelegation) ?? {};

		const resolved: ResolvedWorkerDelegationSettings = {
			enabled: typeof configured.enabled === "boolean" ? configured.enabled : DEFAULT_WORKER_DELEGATION_ENABLED,
			maxUsd: sanitizeNumberSetting(
				configured.maxUsd,
				DEFAULT_WORKER_DELEGATION_MAX_USD,
				0,
				MAX_WORKER_DELEGATION_MAX_USD,
			),
			maxWallClockMs: sanitizeIntegerSetting(
				configured.maxWallClockMs,
				DEFAULT_WORKER_DELEGATION_MAX_WALL_CLOCK_MS,
				0,
				MAX_WORKER_DELEGATION_MAX_WALL_CLOCK_MS,
			),
			writeEnabled:
				typeof configured.writeEnabled === "boolean"
					? configured.writeEnabled
					: DEFAULT_WORKER_DELEGATION_WRITE_ENABLED,
			maxConcurrent: sanitizeIntegerSetting(
				configured.maxConcurrent,
				DEFAULT_WORKER_DELEGATION_MAX_CONCURRENT,
				1,
				MAX_WORKER_DELEGATION_MAX_CONCURRENT,
			),
		};
		if (typeof configured.orchestrationProfile === "string" && configured.orchestrationProfile.trim().length > 0) {
			resolved.orchestrationProfile = configured.orchestrationProfile.trim();
		}
		return resolved;
	}

	getWorkerModelPinPolicy(): WorkerModelPinPolicy {
		return compileWorkerModelPinPolicy({
			global: this.globalSettings.workerDelegation?.modelPins,
			project: this.projectSettings.workerDelegation?.modelPins,
			directoryProfile: this.directoryProfileSettings.workerDelegation?.modelPins,
		});
	}

	getActiveOrchestrationProfile(): string | undefined {
		const profileId = this.settings.activeOrchestrationProfile;
		return typeof profileId === "string" && profileId.trim().length > 0 ? profileId.trim() : undefined;
	}

	setWorkerDelegationSettings(settings: WorkerDelegationSettings, scope: SettingsScope = "global"): void {
		const normalizedSettings = normalizeWorkerDelegationLayer(settings) ?? {};
		const sourceSettings = scope === "project" ? this.projectSettings : this.globalSettings;
		const existingPins = sourceSettings.workerDelegation?.modelPins;
		if (Object.hasOwn(settings, "modelPins")) {
			const policy = compileWorkerModelPinPolicy({ [scope]: settings.modelPins });
			if (policy.status === "invalid") {
				throw new Error(`Invalid worker model pin settings: ${policy.diagnostics.join("; ")}`);
			}
			if (settings.modelPins !== undefined) normalizedSettings.modelPins = structuredClone(settings.modelPins);
		} else if (existingPins !== undefined) {
			normalizedSettings.modelPins = structuredClone(existingPins);
		}
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.workerDelegation = normalizedSettings;
			this.markProjectModified("workerDelegation");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.workerDelegation = normalizedSettings;
		this.markModified("workerDelegation");
		this.save();
	}

	getLearningPolicySettings(): ResolvedLearningPolicySettings {
		const configured = this.settings.learningPolicy ?? {};

		const allowedLayers = Array.isArray(configured.allowedAutoApplyLayers)
			? configured.allowedAutoApplyLayers.filter(
					(layer): layer is LearningPolicyLayer =>
						typeof layer === "string" &&
						["memory", "skill", "prompt", "extension", "tool", "script", "settings", "source"].includes(layer),
				)
			: [...DEFAULT_LEARNING_POLICY_ALLOWED_AUTO_APPLY_LAYERS];

		return {
			enabled: typeof configured.enabled === "boolean" ? configured.enabled : DEFAULT_LEARNING_POLICY_ENABLED,
			autoApplyEnabled:
				typeof configured.autoApplyEnabled === "boolean"
					? configured.autoApplyEnabled
					: DEFAULT_LEARNING_POLICY_AUTO_APPLY_ENABLED,
			confidenceThreshold: sanitizeIntegerSetting(
				configured.confidenceThreshold,
				DEFAULT_LEARNING_POLICY_CONFIDENCE_THRESHOLD,
				0,
				100,
			),
			minObservations: sanitizeIntegerSetting(
				configured.minObservations,
				DEFAULT_LEARNING_POLICY_MIN_OBSERVATIONS,
				0,
				100,
			),
			allowedAutoApplyLayers: allowedLayers,
			requireRollbackPlan:
				typeof configured.requireRollbackPlan === "boolean" ? configured.requireRollbackPlan : true,
			reflectionSourceConfidence: sanitizeIntegerSetting(
				configured.reflectionSourceConfidence,
				DEFAULT_LEARNING_POLICY_REFLECTION_SOURCE_CONFIDENCE,
				0,
				100,
			),
			autoApplySupersessions:
				typeof configured.autoApplySupersessions === "boolean"
					? configured.autoApplySupersessions
					: DEFAULT_LEARNING_POLICY_AUTO_APPLY_SUPERSESSIONS,
		};
	}

	getToolkitScripts(): ToolkitScript[] {
		const configured = this.settings.toolkit?.scripts;
		if (!Array.isArray(configured)) return [];
		return configured.filter(
			(script): script is ToolkitScript =>
				Boolean(script) &&
				typeof script.name === "string" &&
				script.name.length > 0 &&
				typeof script.description === "string" &&
				typeof script.path === "string" &&
				(script.runner === "uv" || script.runner === "powershell" || script.runner === "bash"),
		);
	}

	setToolkitSettings(settings: ToolkitSettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.toolkit = { ...settings };
			this.markProjectModified("toolkit");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.toolkit = { ...settings };
		this.markModified("toolkit");
		this.save();
	}

	getModelCapabilitySettings(): Required<ModelCapabilitySettings> {
		const configured = this.settings.modelCapability?.mode;
		const mode: ModelCapabilityMode =
			configured === "auto" ||
			configured === "off" ||
			configured === "full" ||
			configured === "lean" ||
			configured === "minimal" ||
			configured === "chat"
				? configured
				: DEFAULT_MODEL_CAPABILITY_MODE;
		return { mode };
	}

	setModelCapabilitySettings(settings: ModelCapabilitySettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.modelCapability = { ...settings };
			this.markProjectModified("modelCapability");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.modelCapability = { ...settings };
		this.markModified("modelCapability");
		this.save();
	}

	getBedrockScopeSettings(): BedrockScopeSettings | undefined {
		const scope = normalizeBedrockScopeSettings(this.globalSettings.bedrock);
		return scope
			? {
					...scope,
					modelIds: [...scope.modelIds],
				}
			: undefined;
	}

	setBedrockScopeSettings(scope: BedrockScopeSettings): void {
		const normalized = normalizeBedrockScopeSettings(scope);
		if (!normalized) throw new Error("Cannot persist an invalid or empty Bedrock verification scope.");
		this.globalSettings.bedrock = { ...normalized, modelIds: [...normalized.modelIds] };
		this.markModified("bedrock");
		this.save();
	}

	clearBedrockScopeSettings(): void {
		delete this.globalSettings.bedrock;
		this.markModified("bedrock");
		this.save();
	}

	setLearningPolicySettings(settings: LearningPolicySettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.learningPolicy = { ...settings };
			this.markProjectModified("learningPolicy");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.learningPolicy = { ...settings };
		this.markModified("learningPolicy");
		this.save();
	}

	getAutoLearnSettings(): AutoLearnSettings {
		const settings = this.settings.autoLearn ?? {};
		return {
			...settings,
			model: settings.model ?? this.getModelRouterSettings().learningModel,
			thinkingLevel: settings.thinkingLevel ?? "low",
			complexTaskToolCalls: settings.complexTaskToolCalls ?? 12,
		};
	}

	setAutoLearnSettings(settings: AutoLearnSettings, scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.autoLearn = { ...settings };
			this.markProjectModified("autoLearn");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.autoLearn = { ...settings };
		this.markModified("autoLearn");
		this.save();
	}

	getExternalResourceRoots(): string[] {
		return this.settings.externalResourceRoots ?? [];
	}

	setExternalResourceRoots(roots: string[], scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.externalResourceRoots = [...roots];
			this.markProjectModified("externalResourceRoots");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.externalResourceRoots = [...roots];
		this.markModified("externalResourceRoots");
		this.save();
	}

	getTrustedResourceRoots(): string[] {
		return this.settings.trustedResourceRoots ?? [];
	}

	setTrustedResourceRoots(roots: string[], scope: SettingsScope = "global"): void {
		if (scope === "project") {
			const projectSettings = structuredClone(this.projectSettings);
			projectSettings.trustedResourceRoots = [...roots];
			this.markProjectModified("trustedResourceRoots");
			this.saveProjectSettings(projectSettings);
			return;
		}

		this.globalSettings.trustedResourceRoots = [...roots];
		this.markModified("trustedResourceRoots");
		this.save();
	}

	addTrustedResourceRoot(path: string, scope: SettingsScope = "global"): void {
		const canonicalPath = this.canonicalizePath(path);
		if (!canonicalPath) return;

		const current = this.getTrustedResourceRoots();
		if (!current.includes(canonicalPath)) {
			this.setTrustedResourceRoots([...current, canonicalPath], scope);
		}
	}

	canonicalizePath(p: string): string | null {
		try {
			const resolved = resolve(p.replace(/^~/, homedir()));
			if (existsSync(resolved)) {
				return realpathSync(resolved);
			}
			return resolved;
		} catch {
			return null;
		}
	}

	getEffectiveExternalResourceRoots(): string[] {
		const roots = this.getExternalResourceRoots();
		const trusted = this.getTrustedResourceRoots();

		const canonicalTrusted = new Set(
			trusted.map((t) => this.canonicalizePath(t)).filter((t): t is string => t !== null),
		);

		const effective: string[] = [];
		for (const r of roots) {
			const canonicalR = this.canonicalizePath(r);
			if (canonicalR && canonicalR.trim() !== "" && canonicalTrusted.has(canonicalR) && existsSync(canonicalR)) {
				effective.push(canonicalR);
			}
		}
		return effective;
	}
}
